import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  IconButton,
  Card,
  CardBody,
  Badge,
  Progress,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  FormControl,
  FormLabel,
  Select,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Flex,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Textarea,
  Image,
  Code,
  Link,
  Tooltip,
  Checkbox,
  Grid,
  GridItem,
  Stack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter
} from '@chakra-ui/react';
import {
  FiUpload,
  FiFile,
  FiSearch,
  FiTrash2,
  FiPlay,
  FiEye,
  FiRefreshCw,
  FiClock,
  FiX,
  FiAlertCircle,
  FiDatabase,
  FiDownload,
  FiMoreVertical,
  FiFilter,
  FiCopy,
  FiExternalLink,
  FiMaximize2,
  FiInfo,
  FiStar,
  FiZap,
  FiShield,
  FiCheck,
  FiCheckSquare,
  FiSquare,
  FiArchive,
  FiFolderPlus,
  FiChevronRight,
  FiChevronDown
} from 'react-icons/fi';
import { useFindMany, useAction, useEnqueue, useUser, useFindFirst, useGlobalAction } from '@gadgetinc/react';
import { api } from '../api';
import { hasAnyRole, isInstitutionAdministrator } from '../lib/roleUtils';

// Helper function to convert File to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

// Helper function to check if user has admin privileges
const checkIsAdmin = (user) => {
  if (!user) return false;
  if (user?.email === '74spartan@gmail.com') return true;
  const userRoles = user?.roles || user?.roleList || [];
  const hasAdminRole = hasAnyRole(userRoles, ['admin', 'sysadmin', 'system-admin']);
  return hasAdminRole || isInstitutionAdministrator(user);
};

// Helper function to check if user is a system administrator
const checkIsSystemAdmin = (user) => {
  if (!user) return false;
  if (user?.email === '74spartan@gmail.com') return true;
  const userRoles = user?.roles || user?.roleList || [];
  return hasAnyRole(userRoles, ['sysadmin', 'system-admin']);
};

// Unified file data structure
const unifyFileData = (userFiles, shiftProcessingRecords) => {
  const unifiedFiles = [];
  
  // Process user files
  userFiles?.forEach(file => {
    // Find matching shift processing record
    const docRecord = shiftProcessingRecords?.find(doc => 
      doc.userFileId === file.id || 
      doc.originalFile?.fileName === file.originalFilename ||
      doc.originalFile?.fileName === file.title
    );

    unifiedFiles.push({
      ...file,
      type: 'userFile',
      unifiedId: `file_${file.id}`,
      processingRecord: docRecord,
      processingStatus: docRecord?.processingStatus || (file.extractedText?.markdown ? 'basic_processed' : 'unprocessed'),
      confidence: docRecord?.confidence || null,
      hasExtractedText: !!file.extractedText?.markdown,
      hasAIAnalysis: !!docRecord?.assistantAnalysis?.markdown,
      documentType: docRecord?.documentType || 'unknown'
    });
  });

  // Add any shift processing records that don't have matching user files
  shiftProcessingRecords?.forEach(doc => {
    const existingFile = unifiedFiles.find(f => 
      f.processingRecord?.id === doc.id ||
      (doc.userFileId && f.id === doc.userFileId) ||
      (doc.originalFile?.fileName && (f.originalFilename === doc.originalFile.fileName || f.title === doc.originalFile.fileName))
    );
    
    if (!existingFile) {
      unifiedFiles.push({
        id: `doc_${doc.id}`,
        type: 'shiftProcessing',
        unifiedId: `doc_${doc.id}`,
        originalFilename: doc.originalFile?.fileName || 'Unknown Document',
        title: doc.originalFile?.fileName || 'Unknown Document',
        mimeType: 'application/processed-document',
        fileSizeBytes: null,
        status: 'processed',
        isPublic: true,
        institution: doc.submittedBy?.institution || 'Unknown',
        createdAt: doc.createdAt,
        updatedAt: doc.updatedAt,
        category: 'processed_document',
        processingRecord: doc,
        processingStatus: doc.processingStatus,
        confidence: doc.confidence,
        hasExtractedText: !!doc.extractedText?.markdown,
        hasAIAnalysis: !!doc.assistantAnalysis?.markdown,
        documentType: doc.documentType || 'unknown',
        extractedText: doc.extractedText,
        file: { url: doc.originalFile?.url }
      });
    }
  });

  return unifiedFiles.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
};

// Get processing status display info
const getProcessingStatusInfo = (file, institutionConfig, isSystemAdmin) => {
  const hasAIProcessing = isSystemAdmin || institutionConfig?.enableFisdapShiftProcessing || false;
  
  if (file.processingStatus === 'analysis_complete') {
    return {
      status: 'AI Analysis Complete',
      color: 'purple',
      description: `Confidence: ${(file.confidence || 0).toFixed(1)}%`,
      icon: <FiZap />, 
      priority: 5
    };
  }
  
  if (file.hasExtractedText && !file.hasAIAnalysis) {
    return {
      status: 'Text Extracted',
      color: 'green',
      description: hasAIProcessing ? 'Ready for AI upgrade' : 'Basic processing complete',
      icon: <FiFile />, 
      priority: 4,
      canUpgradeToAI: hasAIProcessing
    };
  }
  
  if (file.processingStatus === 'failed') {
    return {
      status: 'Processing Failed',
      color: 'red',
      description: 'Ready for reprocessing',
      icon: <FiAlertCircle />, 
      priority: 1
    };
  }
  
  if (file.processingStatus === 'requires_review') {
    return {
      status: 'Needs Review',
      color: 'orange',
      description: 'Manual review required',
      icon: <FiClock />, 
      priority: 2
    };
  }
  
  if (['extracting_text', 'analyzing', 'processing'].includes(file.processingStatus)) {
    return {
      status: 'Processing',
      color: 'blue',
      description: hasAIProcessing ? 'AI processing in progress' : 'Processing in progress',
      icon: <FiRefreshCw />, 
      priority: 3,
      isProcessing: true
    };
  }
  
  if (file.status === 'active' && file.processingStatus === 'unprocessed') {
    return {
      status: 'Ready to Process',
      color: 'gray',
      description: hasAIProcessing ? 'AI processing available' : 'Basic processing available',
      icon: <FiPlay />, 
      priority: 0
    };
  }
  
  return {
    status: 'Unknown',
    color: 'gray',
    description: 'Status unavailable',
    icon: <FiClock />, 
    priority: 0
  };
};

// Upload Area Component
const UploadArea = ({ onUpload, uploading, setUploading }) => {
  const [uploadProgress, setUploadProgress] = useState({});
  const toast = useToast();

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    
    try {
      const uploadPromises = acceptedFiles.map(async (file, index) => {
        try {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { progress: 0, status: 'uploading' }
          }));

          await onUpload(file);
          
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { progress: 100, status: 'complete' }
          }));
        } catch (error) {
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { progress: 0, status: 'error', error: error.message }
          }));
          throw error;
        }
      });

      await Promise.allSettled(uploadPromises);
      
      // Clear progress after 3 seconds
      setTimeout(() => {
        setUploadProgress({});
      }, 3000);
      
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  }, [onUpload, setUploading, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    multiple: true
  });

  const progressEntries = Object.entries(uploadProgress);

  return (
    <Card bg="#1e1e1e" border="1px solid #2d2d2d" borderRadius="12px">
      <CardBody p={6}>
        <VStack spacing={4}>
          <div
            {...getRootProps()}
            style={{
              border: `2px dashed ${isDragActive ? '#3b82f6' : '#4b5563'}`,
              borderRadius: '12px',
              padding: '2rem',
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'rgba(59, 130, 246, 0.05)' : 'rgba(75, 85, 99, 0.05)',
              transition: 'all 0.2s ease-in-out',
              width: '100%'
            }}
          >
            <input {...getInputProps()} />
            <VStack spacing={3}>
              <Box color={isDragActive ? '#3b82f6' : '#9ca3af'}>
                <FiUpload size={32} />
              </Box>
              <VStack spacing={1}>
                <Text color="#ffffff" fontWeight="600" fontSize="lg">
                  {isDragActive ? 'Drop files here' : 'Drag & drop files or click to browse'}
                </Text>
                <Text color="#9ca3af" fontSize="sm">
                  PDF, DOC, DOCX, TXT, and image files supported
                </Text>
              </VStack>
            </VStack>
          </div>

          {/* Upload Progress */}
          {progressEntries.length > 0 && (
            <VStack spacing={2} w="100%">
              {progressEntries.map(([fileName, progress]) => (
                <Box key={fileName} w="100%" p={3} bg="#252525" borderRadius="8px" border="1px solid #4b5563">
                  <HStack justify="space-between" mb={2}>
                    <HStack>
                      <FiFile size={14} color="#9ca3af" />
                      <Text fontSize="sm" color="#d1d5db" noOfLines={1}>
                        {fileName}
                      </Text>
                    </HStack>
                    <Badge 
                      colorScheme={
                        progress.status === 'complete' ? 'green' : 
                        progress.status === 'error' ? 'red' : 'blue'
                      }
                      size="sm"
                    >
                      {progress.status === 'complete' ? 'Complete' : 
                       progress.status === 'error' ? 'Error' : 'Uploading'}
                    </Badge>
                  </HStack>
                  {progress.status !== 'error' && (
                    <Progress 
                      value={progress.progress} 
                      colorScheme={progress.status === 'complete' ? 'green' : 'blue'}
                      size="sm"
                      bg="#374151"
                      borderRadius="full"
                    />
                  )}
                  {progress.status === 'error' && (
                    <Text fontSize="xs" color="#ef4444" mt={1}>
                      {progress.error}
                    </Text>
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

// File Filters Component
const FileFiltersPanel = ({ filters, onFiltersChange, fileCount, selectedCount }) => {
  return (
    <Card bg="#1e1e1e" border="1px solid #2d2d2d" borderRadius="12px">
      <CardBody p={4}>
        <VStack spacing={4} align="stretch">
          {/* Search */}
          <FormControl>
            <FormLabel fontSize="sm" color="#9ca3af" mb={2} fontWeight="600">
              <HStack>
                <FiSearch size={14} />
                <Text>Search Files</Text>
              </HStack>
            </FormLabel>
            <Input
              placeholder="Search by filename, content, or metadata..."
              size="sm"
              value={filters.search || ''}
              onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
              bg="#252525"
              border="1px solid #4b5563"
              borderRadius="8px"
              color="#d1d5db"
              _placeholder={{ color: "#9ca3af" }}
              _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
            />
          </FormControl>

          {/* Status Filter */}
          <FormControl>
            <FormLabel fontSize="sm" color="#9ca3af" mb={2} fontWeight="600">Processing Status</FormLabel>
            <Select
              size="sm"
              placeholder="All statuses"
              value={filters.processingStatus || ''}
              onChange={(e) => onFiltersChange({ ...filters, processingStatus: e.target.value })}
              bg="#252525"
              border="1px solid #4b5563"
              borderRadius="8px"
              color="#d1d5db"
              _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
            >
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="unprocessed">Unprocessed</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="processing">Processing</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="basic_processed">Text Extracted</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="analysis_complete">AI Analysis Complete</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="failed">Failed</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="requires_review">Needs Review</option>
            </Select>
          </FormControl>

          {/* File Type Filter */}
          <FormControl>
            <FormLabel fontSize="sm" color="#9ca3af" mb={2} fontWeight="600">File Type</FormLabel>
            <Select
              size="sm"
              placeholder="All types"
              value={filters.fileType || ''}
              onChange={(e) => onFiltersChange({ ...filters, fileType: e.target.value })}
              bg="#252525"
              border="1px solid #4b5563"
              borderRadius="8px"
              color="#d1d5db"
              _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
            >
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="pdf">PDF</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="doc">Word Document</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="text">Text File</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="image">Image</option>
            </Select>
          </FormControl>

          {/* Institution Filter */}
          <FormControl>
            <FormLabel fontSize="sm" color="#9ca3af" mb={2} fontWeight="600">Institution</FormLabel>
            <Select
              size="sm"
              placeholder="All institutions"
              value={filters.institution || ''}
              onChange={(e) => onFiltersChange({ ...filters, institution: e.target.value })}
              bg="#252525"
              border="1px solid #4b5563"
              borderRadius="8px"
              color="#d1d5db"
              _focus={{ borderColor: "#3b82f6", boxShadow: "0 0 0 1px #3b82f6" }}
            >
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="STCC">STCC</option>
              <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="ElevationEMS">ElevationEMS</option>
            </Select>
          </FormControl>

          {/* Quick Filters */}
          <Divider borderColor="#4b5563" />
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" color="#9ca3af" fontWeight="600">Quick Filters</Text>
            <VStack align="stretch" spacing={1}>
              <Checkbox
                isChecked={filters.hasExtractedText || false}
                onChange={(e) => onFiltersChange({ ...filters, hasExtractedText: e.target.checked })}
                colorScheme="green"
                size="sm"
              >
                <Text fontSize="sm" color="#d1d5db">Has Extracted Text</Text>
              </Checkbox>
              <Checkbox
                isChecked={filters.hasAIAnalysis || false}
                onChange={(e) => onFiltersChange({ ...filters, hasAIAnalysis: e.target.checked })}
                colorScheme="purple"
                size="sm"
              >
                <Text fontSize="sm" color="#d1d5db">Has AI Analysis</Text>
              </Checkbox>
              <Checkbox
                isChecked={filters.inVectorStore || false}
                onChange={(e) => onFiltersChange({ ...filters, inVectorStore: e.target.checked })}
                colorScheme="blue"
                size="sm"
              >
                <Text fontSize="sm" color="#d1d5db">In Vector Store</Text>
              </Checkbox>
              <Checkbox
                isChecked={filters.isPrivate || false}
                onChange={(e) => onFiltersChange({ ...filters, isPrivate: e.target.checked })}
                colorScheme="orange"
                size="sm"
              >
                <Text fontSize="sm" color="#d1d5db">Private Files</Text>
              </Checkbox>
            </VStack>
          </VStack>

          {/* File Count Stats */}
          <Divider borderColor="#4b5563" />
          <Box>
            <Text fontSize="sm" color="#9ca3af" fontWeight="600" mb={2}>File Statistics</Text>
            <VStack align="stretch" spacing={1}>
              <HStack justify="space-between">
                <Text fontSize="xs" color="#9ca3af">Total Files:</Text>
                <Text fontSize="xs" color="#d1d5db" fontWeight="500">{fileCount}</Text>
              </HStack>
              {selectedCount > 0 && (
                <HStack justify="space-between">
                  <Text fontSize="xs" color="#9ca3af">Selected:</Text>
                  <Text fontSize="xs" color="#3b82f6" fontWeight="500">{selectedCount}</Text>
                </HStack>
              )}
            </VStack>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );
};

// Bulk Actions Toolbar
const BulkActionsToolbar = ({ selectedFiles, onClearSelection, onBulkProcess, onBulkAddToVectorStore, onBulkDelete }) => {
  if (selectedFiles.length === 0) return null;

  return (
    <Card bg="#1e40af" border="1px solid #3b82f6" borderRadius="12px" mb={4}>
      <CardBody p={4}>
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>
            <HStack spacing={2}>
              <FiCheckSquare size={16} color="white" />
              <Text color="white" fontWeight="600">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
              </Text>
            </HStack>
            <Button
              size="sm"
              leftIcon={<FiPlay />}
              onClick={onBulkProcess}
              bg="white"
              color="#1e40af"
              _hover={{ bg: "#f8fafc" }}
            >
              Process Selected
            </Button>
            <Button
              size="sm"
              leftIcon={<FiDatabase />}
              onClick={onBulkAddToVectorStore}
              bg="#059669"
              color="white"
              _hover={{ bg: "#047857" }}
            >
              Add to Vector Store
            </Button>
            <Button
              size="sm"
              leftIcon={<FiTrash2 />}
              onClick={onBulkDelete}
              bg="#dc2626"
              color="white"
              _hover={{ bg: "#b91c1c" }}
            >
              Delete Selected
            </Button>
          </HStack>
          <Button
            size="sm"
            variant="ghost"
            color="white"
            onClick={onClearSelection}
            _hover={{ bg: "#1e3a8a" }}
          >
            <FiX size={16} />
          </Button>
        </HStack>
      </CardBody>
    </Card>
  );
};

// View Store Contents Modal Component
const ViewStoreContentsModal = ({
  isOpen,
  onClose,
  vectorStore,
  onRemoveFiles,
  onAddFiles,
  onSyncStore
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [removingFiles, setRemovingFiles] = useState([]);
  const [syncingStore, setSyncingStore] = useState(false);
  const user = useUser(api);
  const toast = useToast();

  // Fetch files associated with this vector store
  const [{ data: storeFiles, fetching: filesFetching, error: filesError }, refetchFiles] = useFindMany(api.userFile, {
    filter: vectorStore ? { vectorStoreId: { equals: vectorStore.id } } : { id: { equals: "none" } },
    select: {
      id: true,
      originalFilename: true,
      title: true,
      mimeType: true,
      fileSizeBytes: true,
      status: true,
      createdAt: true,
      processingResults: true,
      extractedText: { markdown: true }
    },
    pause: !vectorStore || !isOpen
  });

  const [{ }, updateFile] = useAction(api.userFile.update);
  const [{ }, synchronizeVectorStore] = useGlobalAction(api.synchronizeVectorStore);

  const handleRemoveFiles = async (fileIds) => {
    if (fileIds.length === 0) return;
    
    if (!vectorStore) {
      toast({
        title: 'Error',
        description: 'Vector store not found',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!window.confirm(`Remove ${fileIds.length} file${fileIds.length > 1 ? 's' : ''} from "${vectorStore.name}"?`)) {
      return;
    }

    setRemovingFiles(fileIds);
    
    try {
      // Update each file to remove vector store association
      await Promise.all(
        fileIds.map(fileId => 
          updateFile({
            id: fileId,
            vectorStore: null
          })
        )
      );

      // Update vector store's localFileIds
      const currentIds = Array.isArray(vectorStore.localFileIds) 
        ? vectorStore.localFileIds 
        : (vectorStore.localFileIds ? JSON.parse(vectorStore.localFileIds) : []);
      
      const updatedIds = currentIds.filter(id => !fileIds.includes(id));
      
      await api.vectorStore.update(vectorStore.id, {
        localFileIds: updatedIds,
        syncStatus: 'pending_sync'
      });

      toast({
        title: 'Files Removed',
        description: `${fileIds.length} file${fileIds.length > 1 ? 's' : ''} removed from vector store`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      setSelectedFiles([]);
      setSelectAll(false);
      refetchFiles();
      
      if (onRemoveFiles) onRemoveFiles(fileIds);

    } catch (error) {
      console.error('Error removing files:', error);
      toast({
        title: 'Failed to Remove Files',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRemovingFiles([]);
    }
  };

  const handleSync = async () => {
    if (!vectorStore) {
      toast({
        title: 'Error',
        description: 'Vector store not found',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setSyncingStore(true);
    
    try {
      await synchronizeVectorStore({
        vectorStoreId: vectorStore.id,
        forceFullSync: true
      });
      
      toast({
        title: 'Sync Initiated',
        description: `"${vectorStore.name}" synchronization started`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      
      if (onSyncStore) onSyncStore(vectorStore);
      
    } catch (error) {
      console.error('Error syncing vector store:', error);
      toast({
        title: 'Sync Failed',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncingStore(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(1)}MB`;
    return `${kb.toFixed(0)}KB`;
  };

  const getProcessingStatusBadge = (file) => {
    const hasExtracted = file.extractedText?.markdown;
    const hasResults = file.processingResults && Object.keys(file.processingResults).length > 0;
    
    if (hasResults) {
      return <Badge colorScheme="purple" size="sm">AI Processed</Badge>;
    } else if (hasExtracted) {
      return <Badge colorScheme="green" size="sm">Text Extracted</Badge>;
    } else {
      return <Badge colorScheme="gray" size="sm">Unprocessed</Badge>;
    }
  };

  if (!vectorStore) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent bg="#1e1e1e" border="1px solid #2d2d2d" maxH="90vh">
        <ModalHeader borderBottom="1px solid #2d2d2d" color="#ffffff">
          <VStack align="start" spacing={2}>
            <Text fontSize="lg" fontWeight="600">
              {vectorStore.name}
            </Text>
            <HStack spacing={4}>
              <Text fontSize="sm" color="#9ca3af">
                {storeFiles?.length || 0} files
              </Text>
              <Text fontSize="sm" color="#9ca3af">
                Status: {vectorStore.status}
              </Text>
              <Text fontSize="sm" color="#9ca3af">
                Sync: {vectorStore.syncStatus?.replace('_', ' ')}
              </Text>
              {vectorStore.openaiVectorStoreId && (
                <Text fontSize="sm" color="#9ca3af">
                  OpenAI ID: {vectorStore.openaiVectorStoreId}
                </Text>
              )}
            </HStack>
          </VStack>
        </ModalHeader>
        <ModalCloseButton color="#9ca3af" />
        
        <ModalBody p={0}>
          <VStack spacing={0} align="stretch">
            {/* Action Bar */}
            <Box bg="#252525" borderBottom="1px solid #2d2d2d" p={4}>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Button
                    size="sm"
                    leftIcon={<FiFolderPlus />}
                    onClick={() => onAddFiles && onAddFiles(vectorStore)}
                    colorScheme="blue"
                  >
                    Add Files
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={syncingStore ? <Spinner size="xs" /> : <FiRefreshCw />}
                    onClick={handleSync}
                    isDisabled={syncingStore}
                    variant="outline"
                    borderColor="#4b5563"
                    color="#d1d5db"
                    _hover={{ bg: "#374151" }}
                  >
                    {syncingStore ? 'Syncing...' : 'Sync with OpenAI'}
                  </Button>
                  {selectedFiles.length > 0 && (
                    <Button
                      size="sm"
                      leftIcon={<FiTrash2 />}
                      onClick={() => handleRemoveFiles(selectedFiles)}
                      colorScheme="red"
                      isLoading={removingFiles.length > 0}
                      loadingText="Removing..."
                    >
                      Remove ({selectedFiles.length})
                    </Button>
                  )}
                </HStack>
                <Text fontSize="sm" color="#9ca3af">
                  {selectedFiles.length} of {storeFiles?.length || 0} files selected
                </Text>
              </HStack>
            </Box>

            {/* Files Table */}
            <Box>
              {filesFetching ? (
                <Flex justify="center" p={8}>
                  <VStack>
                    <Spinner size="lg" />
                    <Text color="#d1d5db">Loading store contents...</Text>
                  </VStack>
                </Flex>
              ) : filesError ? (
                <Alert status="error" m={4}>
                  <AlertIcon />
                  <AlertDescription>Failed to load files: {filesError.message}</AlertDescription>
                </Alert>
              ) : !storeFiles || storeFiles.length === 0 ? (
                <VStack spacing={4} py={8}>
                  <FiDatabase size={48} color="#9ca3af" />
                  <Text color="#d1d5db" fontSize="lg">No files in this vector store</Text>
                  <Text color="#9ca3af" fontSize="sm" textAlign="center">
                    Add files to enable semantic search and AI-powered retrieval
                  </Text>
                  <Button
                    leftIcon={<FiFolderPlus />}
                    onClick={() => onAddFiles && onAddFiles(vectorStore)}
                    colorScheme="blue"
                  >
                    Add Files Now
                  </Button>
                </VStack>
              ) : (
                <Table variant="simple" size="sm">
                  <Thead bg="#252525">
                    <Tr>
                      <Th w="40px" borderColor="#2d2d2d">
                        <Checkbox
                          isChecked={selectAll}
                          onChange={(e) => {
                            setSelectAll(e.target.checked);
                            if (e.target.checked) {
                              setSelectedFiles(storeFiles.map(f => f.id));
                            } else {
                              setSelectedFiles([]);
                            }
                          }}
                          colorScheme="blue"
                        />
                      </Th>
                      <Th fontSize="xs" color="#ffffff" borderColor="#2d2d2d">File Name</Th>
                      <Th fontSize="xs" color="#ffffff" borderColor="#2d2d2d">Type</Th>
                      <Th fontSize="xs" color="#ffffff" borderColor="#2d2d2d">Size</Th>
                      <Th fontSize="xs" color="#ffffff" borderColor="#2d2d2d">Processing</Th>
                      <Th fontSize="xs" color="#ffffff" borderColor="#2d2d2d">Added</Th>
                      <Th fontSize="xs" color="#ffffff" borderColor="#2d2d2d">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {storeFiles.map((file) => (
                      <Tr key={file.id} _hover={{ bg: '#2a2a2a' }}>
                        <Td>
                          <Checkbox
                            isChecked={selectedFiles.includes(file.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFiles(prev => [...prev, file.id]);
                              } else {
                                setSelectedFiles(prev => prev.filter(id => id !== file.id));
                                setSelectAll(false);
                              }
                            }}
                            colorScheme="blue"
                          />
                        </Td>
                        <Td>
                          <HStack>
                            <FiFile color="#9ca3af" size={14} />
                            <Text color="#ffffff" fontSize="sm" noOfLines={1}>
                              {file.originalFilename || file.title || 'Untitled'}
                            </Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Text color="#d1d5db" fontSize="sm">
                            {file.mimeType?.split('/')[1]?.toUpperCase() || 'Unknown'}
                          </Text>
                        </Td>
                        <Td>
                          <Text color="#d1d5db" fontSize="sm">
                            {formatFileSize(file.fileSizeBytes)}
                          </Text>
                        </Td>
                        <Td>
                          {getProcessingStatusBadge(file)}
                        </Td>
                        <Td>
                          <Text color="#9ca3af" fontSize="sm">
                            {new Date(file.createdAt).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td>
                          <Button
                            size="xs"
                            leftIcon={<FiTrash2 />}
                            onClick={() => handleRemoveFiles([file.id])}
                            colorScheme="red"
                            variant="ghost"
                            isLoading={removingFiles.includes(file.id)}
                          >
                            Remove
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid #2d2d2d">
          <Button 
            variant="ghost"
            onClick={onClose}
            color="#9ca3af"
            _hover={{ color: "#d1d5db", bg: "#374151" }}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Add Files to Store Modal Component  
const AddFilesToStoreModal = ({
  isOpen,
  onClose,
  vectorStore,
  onFilesAdded,
  refetchVectorStores
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [addingFiles, setAddingFiles] = useState(false);
  const [filters, setFilters] = useState({});
  const toast = useToast();

  // Fetch files not in any vector store
  const apiFilters = {
    vectorStoreId: { isSet: false }
  };
  
  if (filters.search) {
    // Note: Search would need to be implemented in the API filter or use client-side filtering
  }
  if (filters.status) {
    apiFilters.status = { equals: filters.status };
  }

  const [{ data: availableFiles, fetching, error }, refetch] = useFindMany(api.userFile, {
    filter: apiFilters,
    select: {
      id: true,
      originalFilename: true,
      title: true,
      mimeType: true,
      fileSizeBytes: true,
      status: true,
      processingResults: true,
      extractedText: { markdown: true }
    },
    pause: !isOpen
  });

  const [{ }, updateFile] = useAction(api.userFile.update);

  const handleAddFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    setAddingFiles(true);
    
    try {
      // Update each file to associate with vector store
      await Promise.all(
        selectedFiles.map(fileId => 
          updateFile({
            id: fileId,
            vectorStore: {
              _link: vectorStore.id
            }
          })
        )
      );

      // Update vector store's localFileIds
      const currentIds = Array.isArray(vectorStore.localFileIds) 
        ? vectorStore.localFileIds 
        : (vectorStore.localFileIds ? JSON.parse(vectorStore.localFileIds) : []);
      
      const updatedIds = [...new Set([...currentIds, ...selectedFiles])];
      
      await api.vectorStore.update(vectorStore.id, {
        localFileIds: updatedIds,
        syncStatus: 'pending_sync'
      });

      toast({
        title: 'Files Added',
        description: `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} added to "${vectorStore.name}"`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      setSelectedFiles([]);
      setSelectAll(false);
      refetch();
      
      // Also refetch vector stores to update the cards
      if (refetchVectorStores) {
        refetchVectorStores();
      }
      
      if (onFilesAdded) onFilesAdded(selectedFiles);
      onClose();

    } catch (error) {
      toast({
        title: 'Failed to Add Files',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAddingFiles(false);
    }
  };

  if (!vectorStore) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent bg="#1e1e1e" border="1px solid #2d2d2d">
        <ModalHeader borderBottom="1px solid #2d2d2d" color="#ffffff">
          <Text fontSize="lg" fontWeight="600">
            Add Files to "{vectorStore.name}"
          </Text>
        </ModalHeader>
        <ModalCloseButton color="#9ca3af" />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Filters */}
            <HStack spacing={4}>
              <Input
                placeholder="Search files..."
                value={filters.search || ''}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                bg="#252525"
                border="1px solid #4b5563"
                color="#d1d5db"
                _placeholder={{ color: "#9ca3af" }}
                size="sm"
              />
              <Select
                placeholder="All statuses"
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                bg="#252525"
                border="1px solid #4b5563"
                color="#d1d5db"
                size="sm"
                maxW="150px"
              >
                <option style={{ backgroundColor: "#252525" }} value="active">Active</option>
                <option style={{ backgroundColor: "#252525" }} value="archived">Archived</option>
              </Select>
            </HStack>

            {/* File Selection */}
            <Box border="1px solid #2d2d2d" borderRadius="8px" maxH="400px" overflowY="auto">
              {fetching ? (
                <Flex justify="center" p={8}>
                  <Spinner size="md" />
                </Flex>
              ) : error ? (
                <Alert status="error" m={4}>
                  <AlertIcon />
                  <AlertDescription>{error.message}</AlertDescription>
                </Alert>
              ) : !availableFiles || availableFiles.length === 0 ? (
                <VStack spacing={4} py={8}>
                  <Text color="#9ca3af">No files available to add</Text>
                  <Text fontSize="sm" color="#6b7280">All files are already in vector stores</Text>
                </VStack>
              ) : (
                <Table variant="simple" size="sm">
                  <Thead bg="#252525">
                    <Tr>
                      <Th w="40px">
                        <Checkbox
                          isChecked={selectAll}
                          onChange={(e) => {
                            setSelectAll(e.target.checked);
                            if (e.target.checked) {
                              setSelectedFiles(availableFiles.map(f => f.id));
                            } else {
                              setSelectedFiles([]);
                            }
                          }}
                          colorScheme="blue"
                        />
                      </Th>
                      <Th fontSize="xs" color="#ffffff">File Name</Th>
                      <Th fontSize="xs" color="#ffffff">Type</Th>
                      <Th fontSize="xs" color="#ffffff">Size</Th>
                      <Th fontSize="xs" color="#ffffff">Status</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {availableFiles.map((file) => (
                      <Tr key={file.id}>
                        <Td>
                          <Checkbox
                            isChecked={selectedFiles.includes(file.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFiles(prev => [...prev, file.id]);
                              } else {
                                setSelectedFiles(prev => prev.filter(id => id !== file.id));
                                setSelectAll(false);
                              }
                            }}
                            colorScheme="blue"
                          />
                        </Td>
                        <Td>
                          <HStack>
                            <FiFile color="#9ca3af" size={14} />
                            <Text color="#d1d5db" fontSize="sm">
                              {file.originalFilename || file.title || 'Untitled'}
                            </Text>
                          </HStack>
                        </Td>
                        <Td>
                          <Text color="#d1d5db" fontSize="sm">
                            {file.mimeType?.split('/')[1]?.toUpperCase() || 'Unknown'}
                          </Text>
                        </Td>
                        <Td>
                          <Text color="#d1d5db" fontSize="sm">
                            {file.fileSizeBytes ? `${(file.fileSizeBytes / 1024).toFixed(0)}KB` : 'N/A'}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={file.status === 'active' ? 'green' : 'gray'} size="sm">
                            {file.status}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>

            {selectedFiles.length > 0 && (
              <Alert status="info" size="sm">
                <AlertIcon />
                <AlertDescription>
                  {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected for addition to vector store
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid #2d2d2d">
          <HStack spacing={3}>
            <Button 
              variant="ghost"
              onClick={onClose}
              color="#9ca3af"
              _hover={{ color: "#d1d5db", bg: "#374151" }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleAddFiles}
              isDisabled={selectedFiles.length === 0}
              isLoading={addingFiles}
              loadingText="Adding Files..."
              leftIcon={<FiFolderPlus />}
            >
              Add {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Vector Store Selection Modal Component
const VectorStoreSelectionModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  files = [], 
  isBulk = false,
  loading = false 
}) => {
  const [selectedVectorStore, setSelectedVectorStore] = useState(null);
  const [newStoreName, setNewStoreName] = useState('');
  const [newStoreDescription, setNewStoreDescription] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [creatingStore, setCreatingStore] = useState(false);
  const user = useUser(api);

  // Fetch available vector stores
  const [{ data: vectorStores, fetching: fetchingStores, error: storesError }, refetchStores] = useFindMany(api.vectorStore, {
    filter: {
      isActive: { equals: true }
    },
    select: {
      id: true,
      name: true,
      description: true,
      status: true,
      fileCount: true,
      fileSizeBytes: true,
      createdBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true
      }
    }
  });

  // Create new vector store action
  const [{ }, createVectorStore] = useAction(api.vectorStore.create);

  const handleCreateNewStore = async () => {
    if (!newStoreName.trim()) {
      return;
    }

    setCreatingStore(true);
    try {
      const result = await createVectorStore({
        name: newStoreName.trim(),
        description: newStoreDescription.trim() || `Vector store created for file management`,
        status: 'in_progress',
        isActive: true,
        syncStatus: 'pending_sync',
        openaiVectorStoreId: `temp_${Date.now()}`, // Temp ID until OpenAI creates it
        createdBy: {
          _link: user.id
        }
      });

      if (result.vectorStore) {
        setSelectedVectorStore(result.vectorStore);
        setIsCreatingNew(false);
        setNewStoreName('');
        setNewStoreDescription('');
        refetchStores();
      }
    } catch (error) {
      console.error('Error creating vector store:', error);
    } finally {
      setCreatingStore(false);
    }
  };

  const getSupportedFileTypes = () => {
    const supportedTypes = ['pdf', 'txt', 'docx', 'doc', 'md', 'json'];
    const fileTypes = files.map(file => {
      const ext = file.originalFilename?.split('.').pop()?.toLowerCase() || 
                  file.mimeType?.split('/').pop()?.toLowerCase();
      return ext;
    });
    
    const unsupportedFiles = fileTypes.filter(type => !supportedTypes.includes(type));
    return { supportedTypes, fileTypes, unsupportedFiles };
  };

  const { supportedTypes, unsupportedFiles } = getSupportedFileTypes();
  const hasUnsupportedFiles = unsupportedFiles.length > 0;

  const handleConfirm = () => {
    if (selectedVectorStore && onConfirm) {
      onConfirm(selectedVectorStore);
    }
    onClose();
  };

  const resetModal = () => {
    setSelectedVectorStore(null);
    setIsCreatingNew(false);
    setNewStoreName('');
    setNewStoreDescription('');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent bg="#1e1e1e" border="1px solid #2d2d2d">
        <ModalHeader borderBottom="1px solid #2d2d2d" color="#ffffff">
          <VStack align="start" spacing={1}>
            <Text fontSize="lg" fontWeight="600">
              {isBulk ? `Add ${files.length} Files to Vector Store` : 'Add File to Vector Store'}
            </Text>
            <Text fontSize="sm" color="#9ca3af">
              Vector stores enable semantic search and AI-powered document retrieval
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton color="#9ca3af" />
        
        <ModalBody p={6}>
          <VStack spacing={6} align="stretch">
            {/* File Compatibility Warning */}
            {hasUnsupportedFiles && (
              <Alert status="warning" bg="#451a03" border="1px solid #d97706">
                <AlertIcon />
                <Box>
                  <AlertTitle>Some files may not be supported!</AlertTitle>
                  <AlertDescription>
                    OpenAI vector stores support: {supportedTypes.join(', ')}. 
                    Unsupported files will be skipped.
                  </AlertDescription>
                </Box>
              </Alert>
            )}

            {/* Selected Files Preview */}
            <Box>
              <Text fontSize="sm" fontWeight="600" color="#ffffff" mb={2}>
                {isBulk ? `Selected Files (${files.length})` : 'Selected File'}
              </Text>
              <Box maxH="120px" overflowY="auto" bg="#252525" border="1px solid #4b5563" borderRadius="6px" p={3}>
                <VStack spacing={2} align="stretch">
                  {files.slice(0, isBulk ? 5 : 1).map((file, idx) => (
                    <HStack key={idx} justify="space-between">
                      <HStack>
                        <FiFile size={14} color="#9ca3af" />
                        <Text fontSize="sm" color="#d1d5db">
                          {file.originalFilename || file.title || 'Untitled'}
                        </Text>
                      </HStack>
                      <Text fontSize="xs" color="#9ca3af">
                        {((file.fileSizeBytes || 0) / 1024).toFixed(0)}KB
                      </Text>
                    </HStack>
                  ))}
                  {isBulk && files.length > 5 && (
                    <Text fontSize="xs" color="#9ca3af" textAlign="center">
                      ... and {files.length - 5} more files
                    </Text>
                  )}
                </VStack>
              </Box>
            </Box>

            {/* Vector Store Selection */}
            <Box>
              <HStack justify="space-between" mb={3}>
                <Text fontSize="sm" fontWeight="600" color="#ffffff">
                  Select Vector Store
                </Text>
                <Button
                  size="xs"
                  leftIcon={<FiDatabase />}
                  onClick={() => setIsCreatingNew(!isCreatingNew)}
                  variant={isCreatingNew ? "solid" : "outline"}
                  colorScheme="blue"
                >
                  {isCreatingNew ? 'Cancel' : 'New Store'}
                </Button>
              </HStack>

              {isCreatingNew ? (
                <VStack spacing={3} align="stretch" p={4} bg="#252525" borderRadius="8px" border="1px solid #4b5563">
                  <FormControl>
                    <FormLabel fontSize="sm" color="#d1d5db">Store Name</FormLabel>
                    <Input
                      size="sm"
                      value={newStoreName}
                      onChange={(e) => setNewStoreName(e.target.value)}
                      placeholder="Enter vector store name..."
                      bg="#1a1a1a"
                      border="1px solid #4b5563"
                      color="#d1d5db"
                      _placeholder={{ color: "#9ca3af" }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" color="#d1d5db">Description (Optional)</FormLabel>
                    <Textarea
                      size="sm"
                      value={newStoreDescription}
                      onChange={(e) => setNewStoreDescription(e.target.value)}
                      placeholder="Describe the purpose of this vector store..."
                      bg="#1a1a1a"
                      border="1px solid #4b5563"
                      color="#d1d5db"
                      _placeholder={{ color: "#9ca3af" }}
                      rows={2}
                    />
                  </FormControl>
                  <Button
                    size="sm"
                    leftIcon={<FiDatabase />}
                    onClick={handleCreateNewStore}
                    isLoading={creatingStore}
                    loadingText="Creating..."
                    colorScheme="blue"
                    isDisabled={!newStoreName.trim()}
                  >
                    Create Vector Store
                  </Button>
                </VStack>
              ) : (
                <Box>
                  {fetchingStores ? (
                    <Flex justify="center" p={4}>
                      <VStack>
                        <Spinner size="md" />
                        <Text fontSize="sm" color="#9ca3af">Loading vector stores...</Text>
                      </VStack>
                    </Flex>
                  ) : storesError ? (
                    <Alert status="error">
                      <AlertIcon />
                      <AlertDescription>Failed to load vector stores: {storesError.message}</AlertDescription>
                    </Alert>
                  ) : !vectorStores || vectorStores.length === 0 ? (
                    <Box textAlign="center" p={6} bg="#252525" borderRadius="8px" border="1px solid #4b5563">
                      <VStack spacing={3}>
                        <FiDatabase size={32} color="#9ca3af" />
                        <Text color="#d1d5db">No vector stores available</Text>
                        <Text fontSize="sm" color="#9ca3af">Create a new vector store to get started</Text>
                      </VStack>
                    </Box>
                  ) : (
                    <VStack spacing={2} align="stretch" maxH="200px" overflowY="auto">
                      {vectorStores.map((store) => (
                        <Box
                          key={store.id}
                          p={3}
                          bg={selectedVectorStore?.id === store.id ? "#1e40af" : "#252525"}
                          border={`1px solid ${selectedVectorStore?.id === store.id ? "#3b82f6" : "#4b5563"}`} 
                          borderRadius="8px"
                          cursor="pointer"
                          onClick={() => setSelectedVectorStore(store)}
                          _hover={{ 
                            bg: selectedVectorStore?.id === store.id ? "#1e40af" : "#374151" 
                          }}
                          transition="all 0.2s"
                        >
                          <VStack align="start" spacing={1}>
                            <HStack justify="space-between" w="100%">
                              <Text fontSize="sm" fontWeight="600" color="#ffffff">
                                {store.name}
                              </Text>
                              <Badge colorScheme={store.status === 'completed' ? 'green' : 'blue'} size="sm">
                                {store.status}
                              </Badge>
                            </HStack>
                            {store.description && (
                              <Text fontSize="xs" color="#9ca3af" noOfLines={1}>
                                {store.description}
                              </Text>
                            )}
                            <HStack spacing={4}>
                              <Text fontSize="xs" color="#9ca3af">
                                {store.fileCount || 0} files
                              </Text>
                              <Text fontSize="xs" color="#9ca3af">
                                {store.fileSizeBytes ? `${(store.fileSizeBytes / 1024 / 1024).toFixed(1)}MB` : '0MB'}
                              </Text>
                            </HStack>
                          </VStack>
                        </Box>
                      ))}
                    </VStack>
                  )}
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid #2d2d2d">
          <HStack spacing={3}>
            <Button 
              variant="ghost" 
              onClick={handleClose}
              color="#9ca3af"
              _hover={{ color: "#d1d5db", bg: "#374151" }}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleConfirm}
              isDisabled={!selectedVectorStore}
              isLoading={loading}
              loadingText={isBulk ? "Adding Files..." : "Adding File..."}
              leftIcon={<FiDatabase />}
            >
              {isBulk ? `Add ${files.length} Files` : 'Add File'}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// AI Processing Status Header Component
const AIProcessingStatusHeader = ({ user, institutionConfig, onEnableAI }) => {
  const isAdmin = checkIsAdmin(user);
  const isSystemAdmin = checkIsSystemAdmin(user);
  
  // System admins have unlimited AI processing
  const hasAIProcessing = isSystemAdmin || institutionConfig?.hasClinicalDocumentProcessing || false;
  
  const getStatusText = () => {
    if (isSystemAdmin) return 'System Administrator - Unlimited AI Processing';
    if (hasAIProcessing) return 'Premium AI document analysis available';
    return 'Basic processing only';
  };

  const getBadgeText = () => {
    if (isSystemAdmin) return 'SYSTEM ADMIN';
    return 'PREMIUM';
  };

  const getBadgeColor = () => {
    if (isSystemAdmin) return 'purple';
    return 'green';
  };

  return (
    <Card bg="#1e1e1e" border="1px solid #2d2d2d" borderRadius="12px" mb={6}>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {/* AI Processing Status */}
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Box p={2} borderRadius="full" bg={hasAIProcessing ? "#059669" : "#6b7280"}>
                <FiZap color="white" size={16} />
              </Box>
              <VStack align="start" spacing={0}>
                <Heading size="md" color="#ffffff">
                  AI Processing: {hasAIProcessing ? 'Enabled' : 'Disabled'}
                </Heading>
                <Text fontSize="sm" color="#9ca3af">
                  {getStatusText()}
                </Text>
              </VStack>
            </HStack>
            
            {/* Admin Controls - Only show enable button for regular admins without AI processing */}
            {isAdmin && !isSystemAdmin && !hasAIProcessing && (
              <Button
                leftIcon={<FiStar />}
                colorScheme="blue"
                variant="solid"
                onClick={onEnableAI}
                size="sm"
              >
                Enable AI Processing
              </Button>
            )}
            
            {hasAIProcessing && (
              <Badge colorScheme={getBadgeColor()} p={2} borderRadius="md">
                <HStack spacing={2}>
                  {isSystemAdmin ? <FiShield size={12} /> : <FiShield size={12} />}
                  <Text fontSize="xs" fontWeight="600">{getBadgeText()}</Text>
                </HStack>
              </Badge>
            )}
          </HStack>

          {/* Usage Statistics */}
          {hasAIProcessing ? (
            <Box>
              <HStack justify="space-between" mb={2}>
                <Text fontSize="sm" color="#d1d5db" fontWeight="500">
                  AI Processing Status
                </Text>
                <Text fontSize="sm" color="#9ca3af">
                  {isSystemAdmin ? 'Unlimited Access' : 'Enabled'}
                </Text>
              </HStack>
              {isSystemAdmin && (
                <Box 
                  bg="#7c3aed" 
                  borderRadius="full" 
                  height="8px" 
                  position="relative"
                  overflow="hidden"
                >
                  <Box 
                    bg="linear-gradient(90deg, #8b5cf6, #a855f7, #8b5cf6)"
                    height="100%"
                    width="100%"
                    bgSize="200% 100%"
                    animation="shimmer 2s linear infinite"
                  />
                </Box>
              )}
              {!isSystemAdmin && (
                <Box 
                  bg="#059669" 
                  borderRadius="full" 
                  height="8px"
                />
              )}
            </Box>
          ) : (
            <Alert status="info" size="sm">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">
                  {isSystemAdmin ? 'System Administrator Access' : 'Upgrade to Premium AI Processing'}
                </AlertTitle>
                <AlertDescription fontSize="xs" mt={1}>
                  {isSystemAdmin 
                    ? 'You have unlimited access to all AI processing features as a system administrator.'
                    : 'Get advanced AI-powered document analysis with higher accuracy, automatic classification, and intelligent data extraction.'
                  }
                </AlertDescription>
                {isAdmin && !isSystemAdmin && (
                  <Button
                    size="xs"
                    colorScheme="blue"
                    variant="outline"
                    mt={2}
                    onClick={onEnableAI}
                    leftIcon={<FiStar />}
                  >
                    Enable Now
                  </Button>
                )}
              </Box>
            </Alert>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

// File Preview Modal Component
const FilePreviewModal = ({ isOpen, onClose, fileData, documentData, type = 'file' }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toast = useToast();

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Failed to copy',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const downloadFile = () => {
    if (fileData?.file?.url) {
      window.open(fileData.file.url, '_blank');
    } else {
      toast({
        title: 'Download not available',
        description: 'File URL not found',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${kb.toFixed(1)} KB`;
  };

  const renderFileContent = () => {
    if (!fileData) return null;

    const mimeType = fileData.mimeType?.toLowerCase();
    const fileName = fileData.originalFilename || fileData.title;

    // PDF Preview
    if (mimeType?.includes('pdf')) {
      return (
        <Box>
          <Text mb={4} color="#9ca3af" fontSize="sm">PDF Preview</Text>
          {fileData.file?.url ? (
            <iframe
              src={fileData.file.url}
              width="100%"
              height="600px"
              style={{ border: '1px solid #4b5563', borderRadius: '8px' }}
              title={fileName}
            />
          ) : (
            <Alert status="info">
              <AlertIcon />
              <AlertDescription>PDF preview not available. File URL not found.</AlertDescription>
            </Alert>
          )}
        </Box>
      );
    }

    // Image Preview
    if (mimeType?.includes('image')) {
      return (
        <Box>
          <Text mb={4} color="#9ca3af" fontSize="sm">Image Preview</Text>
          {fileData.file?.url ? (
            <Image
              src={fileData.file.url}
              alt={fileName}
              maxW="100%"
              maxH="600px"
              objectFit="contain"
              border="1px solid #4b5563"
              borderRadius="8px"
            />
          ) : (
            <Alert status="info">
              <AlertIcon />
              <AlertDescription>Image preview not available. File URL not found.</AlertDescription>
            </Alert>
          )}
        </Box>
      );
    }

    // Text files or extracted text
    if (mimeType?.includes('text') || fileData.extractedText?.markdown) {
      const textContent = fileData.extractedText?.markdown || 'Text content not available';
      return (
        <Box>
          <HStack justify="space-between" mb={4}>
            <Text color="#9ca3af" fontSize="sm">
              {mimeType?.includes('text') ? 'Text File Content' : 'Extracted Text Content'}
            </Text>
            <Button
              size="xs"
              leftIcon={<FiCopy />}
              onClick={() => copyToClipboard(textContent)}
              bg="#3b82f6"
              color="white"
              _hover={{ bg: "#2563eb" }}
            >
              Copy
            </Button>
          </HStack>
          <Box
            bg="#1a1a1a"
            border="1px solid #4b5563"
            borderRadius="8px"
            p={4}
            maxH="500px"
            overflowY="auto"
          >
            <Code
              display="block"
              whiteSpace="pre-wrap"
              bg="transparent"
              color="#d1d5db"
              fontSize="sm"
              p={0}
            >
              {textContent}
            </Code>
          </Box>
        </Box>
      );
    }

    // Default fallback
    return (
      <Alert status="info">
        <AlertIcon />
        <VStack align="start" spacing={2}>
          <AlertTitle>Preview not available</AlertTitle>
          <AlertDescription>
            This file type ({mimeType || 'unknown'}) cannot be previewed in the browser.
          </AlertDescription>
          <Button size="sm" leftIcon={<FiDownload />} onClick={downloadFile}>
            Download File
          </Button>
        </VStack>
      </Alert>
    );
  };

  const renderFileInfo = () => {
    const data = fileData || documentData;
    if (!data) return null;

    return (
      <VStack align="stretch" spacing={4}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box>
            <Text fontSize="sm" fontWeight="600" color="#ffffff" mb={2}>File Information</Text>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Name:</Text>
                <Text fontSize="sm" color="#d1d5db">{data.originalFilename || data.title || 'Unknown'}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Size:</Text>
                <Text fontSize="sm" color="#d1d5db">{formatFileSize(data.fileSizeBytes)}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Type:</Text>
                <Text fontSize="sm" color="#d1d5db">{data.mimeType || 'Unknown'}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Status:</Text>
                <Badge colorScheme={data.status === 'active' ? 'green' : 'gray'}>
                  {data.status || 'Unknown'}
                </Badge>
              </HStack>
            </VStack>
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="600" color="#ffffff" mb={2}>Upload Information</Text>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Uploaded:</Text>
                <Text fontSize="sm" color="#d1d5db">
                  {data.createdAt ? new Date(data.createdAt).toLocaleString() : 'Unknown'}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Institution:</Text>
                <Text fontSize="sm" color="#d1d5db">{data.institution || 'Unknown'}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Category:</Text>
                <Badge variant="outline" colorScheme="blue">
                  {data.category?.replace('_', ' ') || 'Unknown'}
                </Badge>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Privacy:</Text>
                <Badge colorScheme={data.isPublic ? 'green' : 'orange'}>
                  {data.isPublic ? 'Public' : 'Private'}
                </Badge>
              </HStack>
            </VStack>
          </Box>
        </SimpleGrid>
      </VStack>
    );
  };

  const renderProcessingDetails = () => {
    const data = documentData || fileData;
    if (!data) return null;

    const hasProcessingResults = data.processingResults && Object.keys(data.processingResults).length > 0;
    const hasExtractedText = data.extractedText?.markdown;

    return (
      <VStack align="stretch" spacing={4}>
        <Text fontSize="sm" fontWeight="600" color="#ffffff">Processing Status</Text>
        
        {/* Processing Status Overview */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box>
            <Text fontSize="sm" color="#9ca3af" mb={2}>Processing Information</Text>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Status:</Text>
                <Badge colorScheme={data.processingStatus === 'analysis_complete' ? 'green' : 'blue'}>
                  {data.processingStatus?.replace('_', ' ') || 'Unknown'}
                </Badge>
              </HStack>
              {data.confidence && (
                <HStack justify="space-between">
                  <Text fontSize="sm" color="#9ca3af">Confidence:</Text>
                  <HStack>
                    <Text fontSize="sm" color="#d1d5db">{data.confidence.toFixed(1)}%</Text>
                    <Progress value={data.confidence} size="sm" colorScheme="green" w="60px" />
                  </HStack>
                </HStack>
              )}
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Method:</Text>
                <Text fontSize="sm" color="#d1d5db">{data.extractionMethod || 'Unknown'}</Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Has Text:</Text>
                <Badge colorScheme={hasExtractedText ? 'green' : 'gray'}>
                  {hasExtractedText ? 'Yes' : 'No'}
                </Badge>
              </HStack>
            </VStack>
          </Box>
          <Box>
            <Text fontSize="sm" color="#9ca3af" mb={2}>Processing Results</Text>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" color="#9ca3af">Has Results:</Text>
                <Badge colorScheme={hasProcessingResults ? 'green' : 'gray'}>
                  {hasProcessingResults ? 'Yes' : 'No'}
                </Badge>
              </HStack>
              {data.documentType && (
                <HStack justify="space-between">
                  <Text fontSize="sm" color="#9ca3af">Document Type:</Text>
                  <Text fontSize="sm" color="#d1d5db">{data.documentType.replace('_', ' ')}</Text>
                </HStack>
              )}
              {data.priority && (
                <HStack justify="space-between">
                  <Text fontSize="sm" color="#9ca3af">Priority:</Text>
                  <Badge colorScheme={data.priority === 'high' ? 'red' : 'blue'}>
                    {data.priority}
                  </Badge>
                </HStack>
              )}
            </VStack>
          </Box>
        </SimpleGrid>

        {/* Processing Results */}
        {hasProcessingResults && (
          <Box>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" fontWeight="600" color="#ffffff">Processing Results</Text>
              <Button
                size="xs"
                leftIcon={<FiCopy />}
                onClick={() => copyToClipboard(JSON.stringify(data.processingResults, null, 2))}
                bg="#3b82f6"
                color="white"
                _hover={{ bg: "#2563eb" }}
              >
                Copy JSON
              </Button>
            </HStack>
            <Box
              bg="#1a1a1a"
              border="1px solid #4b5563"
              borderRadius="8px"
              p={4}
              maxH="300px"
              overflowY="auto"
            >
              <Code
                display="block"
                whiteSpace="pre-wrap"
                bg="transparent"
                color="#d1d5db"
                fontSize="sm"
                p={0}
              >
                {JSON.stringify(data.processingResults, null, 2)}
              </Code>
            </Box>
          </Box>
        )}
      </VStack>
    );
  };

  const renderAnalysisResults = () => {
    if (!documentData?.assistantAnalysis?.markdown) return null;

    return (
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between">
          <Text fontSize="sm" fontWeight="600" color="#ffffff">AI Analysis</Text>
          <Button
            size="xs"
            leftIcon={<FiCopy />}
            onClick={() => copyToClipboard(documentData.assistantAnalysis.markdown)}
            bg="#3b82f6"
            color="white"
            _hover={{ bg: "#2563eb" }}
          >
            Copy
          </Button>
        </HStack>
        <Box
          bg="#1a1a1a"
          border="1px solid #4b5563"
          borderRadius="8px"
          p={4}
          maxH="500px"
          overflowY="auto"
        >
          <Code
            display="block"
            whiteSpace="pre-wrap"
            bg="transparent"
            color="#d1d5db"
            fontSize="sm"
            p={0}
          >
            {documentData.assistantAnalysis.markdown}
          </Code>
        </Box>
      </VStack>
    );
  };

  const renderErrorLog = () => {
    if (!documentData?.errorLog?.markdown) return null;

    return (
      <VStack align="stretch" spacing={4}>
        <HStack justify="space-between">
          <Text fontSize="sm" fontWeight="600" color="#ffffff">Error Log</Text>
          <Button
            size="xs"
            leftIcon={<FiCopy />}
            onClick={() => copyToClipboard(documentData.errorLog.markdown)}
            bg="#3b82f6"
            color="white"
            _hover={{ bg: "#2563eb" }}
          >
            Copy
          </Button>
        </HStack>
        <Box
          bg="#1a1a1a"
          border="1px solid #ef4444"
          borderRadius="8px"
          p={4}
          maxH="300px"
          overflowY="auto"
        >
          <Code
            display="block"
            whiteSpace="pre-wrap"
            bg="transparent"
            color="#fca5a5"
            fontSize="sm"
            p={0}
          >
            {documentData.errorLog.markdown}
          </Code>
        </Box>
      </VStack>
    );
  };

  const tabs = [
    { label: 'Preview', icon: <FiEye />, content: renderFileContent() },
    { label: 'Details', icon: <FiInfo />, content: renderFileInfo() },
    { label: 'Processing', icon: <FiPlay />, content: renderProcessingDetails() },
  ];

  // Add analysis tab if available
  if (documentData?.assistantAnalysis?.markdown) {
    tabs.push({ label: 'Analysis', icon: <FiFile />, content: renderAnalysisResults() });
  }

  // Add error log tab if available
  if (documentData?.errorLog?.markdown) {
    tabs.push({ label: 'Errors', icon: <FiAlertCircle />, content: renderErrorLog() });
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size={isFullscreen ? "full" : "6xl"}
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent 
        bg="#1e1e1e" 
        border="1px solid #2d2d2d"
        maxH={isFullscreen ? "100vh" : "90vh"}
      >
        <ModalHeader borderBottom="1px solid #2d2d2d" color="#ffffff">
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Text fontSize="lg" fontWeight="600">
                {(fileData?.originalFilename || fileData?.title || documentData?.originalFile?.fileName) || 'File Preview'}
              </Text>
              <Text fontSize="sm" color="#9ca3af">
                {type === 'document' ? 'Processed Document' : 'File'}  {fileData?.mimeType || 'Unknown type'}
              </Text>
            </VStack>
            <HStack>
              <Tooltip label={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                <IconButton
                  icon={<FiMaximize2 />}
                  size="sm"
                  variant="ghost"
                  color="#9ca3af"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                />
              </Tooltip>
              {fileData?.file?.url && (
                <Tooltip label="Download File">
                  <IconButton
                    icon={<FiDownload />}
                    size="sm"
                    variant="ghost"
                    color="#9ca3af"
                    onClick={downloadFile}
                  />
                </Tooltip>
              )}
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="#9ca3af" />
        
        <ModalBody p={0}>
          <Tabs 
            index={activeTab} 
            onChange={setActiveTab}
            variant="enclosed"
            bg="#1e1e1e"
          >
            <TabList bg="#252525" borderBottom="1px solid #2d2d2d">
              {tabs.map((tab, index) => (
                <Tab 
                  key={index}
                  color="#9ca3af" 
                  _selected={{ 
                    color: "#ffffff", 
                    bg: "#3b82f6",
                    borderColor: "#3b82f6"
                  }}
                  _hover={{ color: "#d1d5db" }}
                >
                  <HStack>
                    {tab.icon}
                    <Text>{tab.label}</Text>
                  </HStack>
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {tabs.map((tab, index) => (
                <TabPanel key={index} p={6}>
                  {tab.content}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// File Upload Component
const FileUploadArea = ({ onUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    
    try {
      for (const file of acceptedFiles) {
        await onUpload(file);
      }
      
      showNotification('Files uploaded successfully', 'success');
    } catch (error) {
      showNotification(`Upload failed: ${error.message}`, 'error');
    } finally {
      setUploading(false);
    }
  }, [onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    multiple: true
  });

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {notification && (
        <div style={{
          padding: '1rem',
          marginBottom: '1rem',
          borderRadius: '0.5rem',
          backgroundColor: notification.type === 'error' ? '#dc2626' : notification.type === 'success' ? '#059669' : '#3b82f6',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{notification.message}</span>
          <button 
            onClick={() => setNotification(null)}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '0.25rem' }}
          >
            <FiX size={16} />
          </button>
        </div>
      )}

      <div style={{
        backgroundColor: '#1f2937',
        border: '1px solid #374151',
        borderRadius: '0.75rem',
        padding: '1.5rem'
      }}>
        <div
          {...getRootProps()}
          style={{
            border: `2px dashed ${isDragActive ? '#3b82f6' : '#4b5563'}`,
            borderRadius: '0.75rem',
            padding: '3rem',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'rgba(59, 130, 246, 0.05)' : 'rgba(75, 85, 99, 0.05)',
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <input {...getInputProps()} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <div style={{ color: isDragActive ? '#3b82f6' : '#9ca3af' }}>
              <FiUpload size={32} />
            </div>
            <div style={{ color: 'white', fontWeight: '500', fontSize: '1rem' }}>
              {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to select'}
            </div>
            <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              Supports PDF, DOC, DOCX, TXT, and images
            </div>
          </div>
        </div>

        {uploading && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            marginTop: '1rem',
            justifyContent: 'center'
          }}>
            <div style={{ 
              width: '1rem', 
              height: '1rem', 
              border: '2px solid #3b82f6', 
              borderTop: '2px solid transparent', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite' 
            }} />
            <span style={{ color: '#9ca3af' }}>Uploading files...</span>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// File Filters Component
const FileFilters = ({ filters, onFiltersChange }) => {
  return (
    <Card bg="#1e1e1e" border="1px solid #2d2d2d" borderRadius="12px">
      <CardBody p={4}>
        <VStack spacing={4} align="stretch">
          {/* Main Filters Row */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs" color="#9ca3af" mb={1} fontWeight="500">Search</FormLabel>
              <Box position="relative">
                <Input
                  placeholder="Search files..."
                  size="sm"
                  value={filters.search || ''}
                  onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                  pl={8}
                  bg="#252525"
                  border="1px solid #4b5563"
                  borderRadius="6px"
                  color="#d1d5db"
                  _placeholder={{ color: "#9ca3af" }}
                  _focus={{
                    borderColor: "#3b82f6",
                    boxShadow: "0 0 0 1px #3b82f6"
                  }}
                />
                <Box
                  position="absolute"
                  left="0.5rem"
                  top="50%"
                  transform="translateY(-50%)"
                  color="#9ca3af"
                  pointerEvents="none"
                >
                  <FiSearch size={14} />
                </Box>
              </Box>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="#9ca3af" mb={1} fontWeight="500">Institution</FormLabel>
              <Select
                size="sm"
                placeholder="All institutions"
                value={filters.institution || ''}
                onChange={(e) => onFiltersChange({ ...filters, institution: e.target.value })}
                bg="#252525"
                border="1px solid #4b5563"
                borderRadius="6px"
                color="#d1d5db"
                _placeholder={{ color: "#9ca3af" }}
                _focus={{
                  borderColor: "#3b82f6",
                  boxShadow: "0 0 0 1px #3b82f6"
                }}
              >
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="STCC">STCC</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="ElevationEMS">ElevationEMS</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="Other">Other</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="#9ca3af" mb={1} fontWeight="500">Status</FormLabel>
              <Select
                size="sm"
                placeholder="All statuses"
                value={filters.status || ''}
                onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
                bg="#252525"
                border="1px solid #4b5563"
                borderRadius="6px"
                color="#d1d5db"
                _placeholder={{ color: "#9ca3af" }}
                _focus={{
                  borderColor: "#3b82f6",
                  boxShadow: "0 0 0 1px #3b82f6"
                }}
              >
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="active">Active</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="processing">Processing</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="archived">Archived</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="failed">Failed</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="#9ca3af" mb={1} fontWeight="500">Processing</FormLabel>
              <Select
                size="sm"
                placeholder="All processing states"
                value={filters.processingStatus || ''}
                onChange={(e) => onFiltersChange({ ...filters, processingStatus: e.target.value })}
                bg="#252525"
                border="1px solid #4b5563"
                borderRadius="6px"
                color="#d1d5db"
                _placeholder={{ color: "#9ca3af" }}
                _focus={{
                  borderColor: "#3b82f6",
                  boxShadow: "0 0 0 1px #3b82f6"
                }}
              >
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="unprocessed">Unprocessed</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="processing_eligible">Processing Eligible</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="successfully_processed">Successfully Processed</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="processing_failed">Processing Failed</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="needs_review">Needs Review</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="has_extracted_content">Has Extracted Content</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="#9ca3af" mb={1} fontWeight="500">Category</FormLabel>
              <Select
                size="sm"
                placeholder="All categories"
                value={filters.category || ''}
                onChange={(e) => onFiltersChange({ ...filters, category: e.target.value })}
                bg="#252525"
                border="1px solid #4b5563"
                borderRadius="6px"
                color="#d1d5db"
                _placeholder={{ color: "#9ca3af" }}
                _focus={{
                  borderColor: "#3b82f6",
                  boxShadow: "0 0 0 1px #3b82f6"
                }}
              >
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="uploaded_document">Uploaded Document</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="generated_report">Generated Report</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="clinical_note">Clinical Note</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="patient_encounter">Patient Encounter</option>
                <option style={{ backgroundColor: "#252525", color: "#d1d5db" }} value="processed_document">Processed Document</option>
              </Select>
            </FormControl>
          </SimpleGrid>

          {/* Quick Filters Row */}
          <Box>
            <Text fontSize="xs" color="#9ca3af" mb={2} fontWeight="500">Quick Filters</Text>
            <HStack spacing={2} flexWrap="wrap">
              <Button
                size="xs"
                variant={filters.needsProcessing ? "solid" : "outline"}
                colorScheme="orange"
                onClick={() => onFiltersChange({ ...filters, needsProcessing: !filters.needsProcessing })}
                borderRadius="6px"
                _hover={{
                  bg: filters.needsProcessing ? "orange.600" : "orange.50"
                }}
              >
                Needs Processing
              </Button>
              <Button
                size="xs"
                variant={filters.hasResults ? "solid" : "outline"}
                colorScheme="green"
                onClick={() => onFiltersChange({ ...filters, hasResults: !filters.hasResults })}
                borderRadius="6px"
                _hover={{
                  bg: filters.hasResults ? "green.600" : "green.50"
                }}
              >
                Has Extracted Text
              </Button>
              <Button
                size="xs"
                variant={filters.inVectorStore ? "solid" : "outline"}
                colorScheme="blue"
                onClick={() => onFiltersChange({ ...filters, inVectorStore: !filters.inVectorStore })}
                borderRadius="6px"
                _hover={{
                  bg: filters.inVectorStore ? "blue.600" : "blue.50"
                }}
              >
                In Vector Store
              </Button>
            </HStack>
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );
};

// Enhanced processing status with progress tracking and AI upgrade options
const getDetailedProcessingStatus = (file, shiftProcessingRecords, processingFiles, institutionConfig, isSystemAdmin) => {
  const hasAIProcessing = isSystemAdmin || institutionConfig?.enableFisdapShiftProcessing || false;
  
  // Check if file is currently being processed
  if (processingFiles?.includes(file.id)) {
    const processingStages = [
      { stage: 'uploading', label: 'Uploading file...', progress: 10 },
      { stage: 'extracting', label: 'Extracting text content...', progress: 40 },
      { stage: 'analyzing', label: hasAIProcessing ? 'AI analyzing structure...' : 'Processing document...', progress: 70 },
      { stage: 'finalizing', label: 'Finalizing analysis...', progress: 90 }
    ];
    
    // Simulate current stage (could be enhanced with real progress tracking)
    const currentStage = processingStages[1]; // Default to extracting
    
    return (
      <div style={{ width: '100%', minWidth: '200px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem',
          backgroundColor: '#1e40af',
          borderRadius: '0.5rem',
          border: '1px solid #3b82f6'
        }}>
          <div style={{
            width: '0.75rem',
            height: '0.75rem',
            border: '2px solid white',
            borderTop: '2px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{ flex: 1 }}>
            <div style={{ 
              color: 'white', 
              fontSize: '0.75rem', 
              fontWeight: '600',
              marginBottom: '0.25rem'
            }}>
              {hasAIProcessing ? 'AI Processing' : 'Processing'}
            </div>
            <div style={{ 
              color: '#bfdbfe', 
              fontSize: '0.625rem',
              marginBottom: '0.25rem'
            }}>
              {currentStage.label}
            </div>
            <div style={{
              width: '100%',
              height: '0.25rem',
              backgroundColor: '#1e3a8a',
              borderRadius: '0.125rem',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${currentStage.progress}%`,
                height: '100%',
                backgroundColor: '#60a5fa',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Find linked shift processing record
  let linkedDoc = shiftProcessingRecords?.find(doc => 
    doc.userFileId === file.id || 
    doc.originalFile?.fileName === file.originalFilename ||
    doc.originalFile?.fileName === file.title
  );

  const hasExtractedText = file.extractedText?.markdown;
  const hasValidProcessingResults = file.processingResults && 
    file.processingResults !== null && 
    typeof file.processingResults === 'object' &&
    Object.keys(file.processingResults).length > 0;

  // AI Analysis Complete
  if (linkedDoc?.processingStatus === 'analysis_complete' || (hasAIProcessing && hasValidProcessingResults && hasExtractedText)) {
    const confidence = linkedDoc?.confidence || 85;
    const textLength = hasExtractedText ? file.extractedText.markdown.replace(/<[^>]*>/g, '').length : 0;
    
    return (
      <div style={{ width: '100%', minWidth: '180px' }}>
        <div style={{
          padding: '0.5rem',
          backgroundColor: '#7c3aed',
          borderRadius: '0.5rem',
          border: '1px solid #8b5cf6'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            marginBottom: '0.25rem'
          }}>
            <FiZap size={12} color="white" />
            <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: '600' }}>
              AI Analysis Complete
            </span>
          </div>
          <div style={{ 
            color: '#c4b5fd', 
            fontSize: '0.625rem',
            marginBottom: '0.25rem'
          }}>
            Confidence: {confidence.toFixed(1)}%  {textLength} chars extracted
          </div>
          <div style={{
            width: '100%',
            height: '0.25rem',
            backgroundColor: '#6d28d9',
            borderRadius: '0.125rem',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${confidence}%`,
              height: '100%',
              backgroundColor: '#a78bfa'
            }} />
          </div>
        </div>
      </div>
    );
  }

  // Basic Text Extraction (eligible for AI upgrade)
  if (hasExtractedText && !hasValidProcessingResults) {
    const textLength = file.extractedText.markdown.replace(/<[^>]*>/g, '').length;
    
    return (
      <div style={{ width: '100%', minWidth: '180px' }}>
        <div style={{
          padding: '0.5rem',
          backgroundColor: '#059669',
          borderRadius: '0.5rem',
          border: '1px solid #10b981'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            marginBottom: '0.25rem'
          }}>
            <FiFile size={12} color="white" />
            <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: '600' }}>
              Basic Processing Complete
            </span>
          </div>
          <div style={{ 
            color: '#a7f3d0', 
            fontSize: '0.625rem',
            marginBottom: '0.25rem'
          }}>
            {textLength} chars  {hasAIProcessing ? 'Ready for AI upgrade' : 'Text extracted successfully'}
          </div>
          {hasAIProcessing && (
            <div style={{ 
              color: '#fef3c7', 
              fontSize: '0.625rem',
              backgroundColor: '#d97706',
              padding: '0.125rem 0.25rem',
              borderRadius: '0.25rem',
              display: 'inline-block'
            }}>
               AI Upgrade Available
            </div>
          )}
        </div>
      </div>
    );
  }

  // Processing failed
  if (linkedDoc?.processingStatus === 'failed' || file.status === 'failed') {
    return (
      <div style={{ width: '100%', minWidth: '180px' }}>
        <div style={{
          padding: '0.5rem',
          backgroundColor: '#dc2626',
          borderRadius: '0.5rem',
          border: '1px solid #ef4444'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            marginBottom: '0.25rem'
          }}>
            <FiAlertCircle size={12} color="white" />
            <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: '600' }}>
              Processing Failed
            </span>
          </div>
          <div style={{ 
            color: '#fecaca', 
            fontSize: '0.625rem'
          }}>
            Ready for reprocessing
          </div>
        </div>
      </div>
    );
  }

  // Needs review
  if (linkedDoc?.processingStatus === 'requires_review') {
    return (
      <div style={{ width: '100%', minWidth: '180px' }}>
        <div style={{
          padding: '0.5rem',
          backgroundColor: '#d97706',
          borderRadius: '0.5rem',
          border: '1px solid #f59e0b'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            marginBottom: '0.25rem'
          }}>
            <FiClock size={12} color="white" />
            <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: '600' }}>
              Needs Review
            </span>
          </div>
          <div style={{ 
            color: '#fed7aa', 
            fontSize: '0.625rem'
          }}>
            Manual review required
          </div>
        </div>
      </div>
    );
  }

  // Ready for processing
  const canProcess = file.status === 'active' && (!hasValidProcessingResults && !hasExtractedText);
  if (canProcess) {
    return (
      <div style={{ width: '100%', minWidth: '180px' }}>
        <div style={{
          padding: '0.5rem',
          backgroundColor: '#374151',
          borderRadius: '0.5rem',
          border: '1px solid #4b5563'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            marginBottom: '0.25rem'
          }}>
            <FiPlay size={12} color="white" />
            <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: '600' }}>
              Ready to Process
            </span>
          </div>
          <div style={{ 
            color: '#d1d5db', 
            fontSize: '0.625rem'
          }}>
            {hasAIProcessing ? 'AI analysis available' : 'Basic processing available'}
          </div>
        </div>
      </div>
    );
  }

  // Default pending state
  return (
    <div style={{ width: '100%', minWidth: '180px' }}>
      <div style={{
        padding: '0.5rem',
        backgroundColor: '#4b5563',
        borderRadius: '0.5rem',
        border: '1px solid #6b7280'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem',
          marginBottom: '0.25rem'
        }}>
          <FiClock size={12} color="white" />
          <span style={{ color: 'white', fontSize: '0.75rem', fontWeight: '600' }}>
            Pending
          </span>
        </div>
        <div style={{ 
          color: '#d1d5db', 
          fontSize: '0.625rem'
        }}>
          Awaiting processing
        </div>
      </div>
    </div>
  );
};

// Helper function to get status badge for files
const getStatusBadge = (status) => {
  const statusColors = {
    active: 'green',
    processing: 'blue', 
    failed: 'red',
    archived: 'gray'
  };
  
  const statusLabels = {
    active: 'Active',
    processing: 'Processing',
    failed: 'Failed', 
    archived: 'Archived'
  };
  
  return (
    <Badge 
      colorScheme={statusColors[status] || 'gray'} 
      variant="solid"
      fontSize="10px"
    >
      {statusLabels[status] || status || 'Unknown'}
    </Badge>
  );
};

// Helper function to get processing status badge
const getProcessingStatusBadge = (file, shiftProcessingRecords, processingFiles, institutionConfig, isSystemAdmin) => {
  return getDetailedProcessingStatus(file, shiftProcessingRecords, processingFiles, institutionConfig, isSystemAdmin);
};

// Helper function to get vector store badge
const getVectorStoreBadge = (file) => {
  if (file.vectorStore && file.vectorStore.id) {
    return (
      <Badge 
        colorScheme="green" 
        variant="solid"
        fontSize="10px"
      >
        {file.vectorStore.name || 'In Vector Store'}
      </Badge>
    );
  }
  
  return (
    <Badge 
      colorScheme="gray" 
      variant="outline"
      fontSize="10px"
      borderColor="#4b5563"
      color="#9ca3af"
    >
      Not Added
    </Badge>
  );
};

// File List Table Component with enhanced compact design
const FileListTable = ({ 
  files, 
  loading, 
  onProcess, 
  onDelete, 
  onView, 
  onAddToVectorStore,
  onReprocessWithAI,
  onTogglePrivacy,
  selectedFiles,
  onSelectionChange,
  selectAll,
  onSelectAllChange,
  onBulkProcess,
  onBulkAddToVectorStore,
  shiftProcessingRecords,
  processingFiles,
  reprocessingFiles,
  institutionConfig,
  isSystemAdmin
}) => {
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const kb = bytes / 1024;
    const mb = kb / 1024;
    if (mb >= 1) return `${mb.toFixed(1)}MB`;
    return `${kb.toFixed(0)}KB`;
  };

  const getFileTypeIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return '';
    if (mimeType?.includes('image')) return '🖼️';
    if (mimeType?.includes('word') || mimeType?.includes('doc')) return '📝';
    if (mimeType?.includes('text')) return '';
    return '';
  };

  const canUpgradeToAI = (file, shiftProcessingRecords, institutionConfig, isSystemAdmin) => {
    const hasAIProcessing = isSystemAdmin || institutionConfig?.hasClinicalDocumentProcessing || false;
    if (!hasAIProcessing) return false;
    
    const hasExtractedText = file.extractedText?.markdown;
    const hasValidProcessingResults = file.processingResults && 
      file.processingResults !== null && 
      typeof file.processingResults === 'object' &&
      Object.keys(file.processingResults).length > 0;
    
    // Find linked shift processing record
    const linkedDoc = shiftProcessingRecords?.find(doc => 
      doc.userFileId === file.id || 
      doc.originalFile?.fileName === file.originalFilename ||
      doc.originalFile?.fileName === file.title
    );

    // Only allow upgrade if we have basic text extraction but no AI analysis
    return hasExtractedText && !hasValidProcessingResults && 
           (!linkedDoc || linkedDoc.processingStatus !== 'analysis_complete');
  };

  if (loading) {
    return (
      <Flex justify="center" p={8}>
        <VStack>
          <Spinner size="lg" />
          <Text>Loading files...</Text>
        </VStack>
      </Flex>
    );
  }

  if (!files || files.length === 0) {
    return (
      <Card bg="#1e1e1e" border="1px solid #2d2d2d" borderRadius="12px">
        <CardBody>
          <VStack spacing={4} py={8}>
            <FiFile size={48} color="#9ca3af" />
            <Text color="#d1d5db" fontSize="lg">No files found</Text>
            <Text color="#9ca3af" fontSize="sm" textAlign="center">
              Upload some files to get started
            </Text>
          </VStack>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card bg="#1e1e1e" border="1px solid #2d2d2d" borderRadius="12px" overflow="hidden">
      <CardBody p={0}>
        {/* Bulk Actions Header - Always visible to prevent layout shifts */}
        <Box
          bg={selectedFiles.length > 0 ? "#1e40af" : "#374151"}
          borderBottom="1px solid #2d2d2d"
          p={3}
        >
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Text fontSize="sm" fontWeight="medium" color="#ffffff">
                {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
              </Text>
              <Button
                size="xs"
                leftIcon={<FiPlay />}
                onClick={onBulkProcess}
                bg={selectedFiles.length > 0 ? "#3b82f6" : "#6b7280"}
                color={selectedFiles.length > 0 ? "white" : "#9ca3af"}
                variant="solid"
                borderRadius="6px"
                isDisabled={selectedFiles.length === 0}
                _hover={selectedFiles.length > 0 ? { bg: "#2563eb" } : {}}
                cursor={selectedFiles.length > 0 ? "pointer" : "not-allowed"}
              >
                Process
              </Button>
              <Button
                size="xs"
                leftIcon={<FiDatabase />}
                onClick={onBulkAddToVectorStore}
                bg={selectedFiles.length > 0 ? "#059669" : "#6b7280"}
                color={selectedFiles.length > 0 ? "white" : "#9ca3af"}
                variant="solid"
                borderRadius="6px"
                isDisabled={selectedFiles.length === 0}
                _hover={selectedFiles.length > 0 ? { bg: "#047857" } : {}}
                cursor={selectedFiles.length > 0 ? "pointer" : "not-allowed"}
              >
                Add to Vector
              </Button>
              <Menu>
                <MenuButton 
                  as={Button} 
                  size="xs" 
                  variant="outline" 
                  border={selectedFiles.length > 0 ? "1px solid #3b82f6" : "1px solid #6b7280"}
                  color={selectedFiles.length > 0 ? "#3b82f6" : "#9ca3af"}
                  borderRadius="6px"
                  isDisabled={selectedFiles.length === 0}
                  _hover={selectedFiles.length > 0 ? { bg: "#1e3a8a", color: "white" } : {}}
                  cursor={selectedFiles.length > 0 ? "pointer" : "not-allowed"}
                >
                  Privacy
                </MenuButton>
                <MenuList bg="#252525" border="1px solid #4b5563" zIndex={9999} portal={true}>
                  <MenuItem 
                    bg="#252525" 
                    color="#d1d5db"
                    _hover={{ bg: "#374151" }}
                    onClick={() => onBulkPrivacyChange && onBulkPrivacyChange(selectedFiles, true)}
                    isDisabled={selectedFiles.length === 0}
                  >
                    Make Public
                  </MenuItem>
                  <MenuItem 
                    bg="#252525" 
                    color="#d1d5db"
                    _hover={{ bg: "#374151" }}
                    onClick={() => onBulkPrivacyChange && onBulkPrivacyChange(selectedFiles, false)}
                    isDisabled={selectedFiles.length === 0}
                  >
                    Make Private
                  </MenuItem>
                </MenuList>
              </Menu>
            </HStack>
            <Button
              size="xs"
              variant="ghost"
              color={selectedFiles.length > 0 ? "#d1d5db" : "#9ca3af"}
              isDisabled={selectedFiles.length === 0}
              _hover={selectedFiles.length > 0 ? { bg: "#374151" } : {}}
              cursor={selectedFiles.length > 0 ? "pointer" : "not-allowed"}
              onClick={() => {
                onSelectionChange([]);
                onSelectAllChange(false);
              }}
            >
              Clear Selection
            </Button>
          </HStack>
        </Box>

        {/* Files Table */}
        <Box overflowX="auto" position="relative">
          <Table variant="simple" size="sm">
            <Thead bg="#252525">
              <Tr>
                <Th w="40px" p={2} borderColor="#2d2d2d">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={(e) => onSelectAllChange(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                </Th>
                <Th fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">File Name</Th>
                <Th fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">Type</Th>
                <Th fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">Size</Th>
                <Th fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">Status</Th>
                <Th fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">Processing</Th>
                <Th fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">Vector Store</Th>
                <Th fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">Institution</Th>
                <Th fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">Created</Th>
                <Th w="100px" fontSize="xs" fontWeight="600" color="#ffffff" borderColor="#2d2d2d">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {files.map((file) => {
                const isSelected = selectedFiles.includes(file.id);
                const hasExtractedText = file.extractedText?.markdown;
                const canProcess = file.status === 'active' && (!file.processingResults || 
                  Object.keys(file.processingResults).length === 0);

                return (
                  <Tr key={file.id} _hover={{ bg: '#252525' }} fontSize="sm">
                    <Td p={2}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            onSelectionChange([...selectedFiles, file.id]);
                          } else {
                            onSelectionChange(selectedFiles.filter(id => id !== file.id));
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                    </Td>
                    <Td p={2}>
                      <VStack align="start" spacing={1}>
                        <HStack>
                          <FiFile color="#9ca3af" size={14} />
                          <Text fontWeight="500" fontSize="sm" noOfLines={1} color="#ffffff">
                            {file.originalFilename || file.title || 'Untitled'}
                          </Text>
                        </HStack>
                        <HStack spacing={2}>
                          <Badge size="xs" colorScheme={file.isPublic ? 'green' : 'orange'} fontSize="10px">
                            {file.isPublic ? 'Public' : 'Private'}
                          </Badge>
                          {file.category && (
                            <Badge size="xs" variant="outline" fontSize="10px" color="#9ca3af" borderColor="#4b5563">
                              {file.category.replace('_', ' ')}
                            </Badge>
                          )}
                        </HStack>
                      </VStack>
                    </Td>
                    <Td p={2}>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="500" color="#d1d5db">
                          {file.mimeType?.split('/')[1]?.toUpperCase() || 'Unknown'}
                        </Text>
                        <Text fontSize="xs" color="#9ca3af" noOfLines={1}>
                          {file.mimeType}
                        </Text>
                      </VStack>
                    </Td>
                    <Td p={2}>
                      <Text fontSize="sm" color="#d1d5db">{formatFileSize(file.fileSizeBytes)}</Text>
                    </Td>
                    <Td p={2}>
                      {getStatusBadge(file.status)}
                    </Td>
                    <Td p={2}>
                      {getProcessingStatusBadge(file, shiftProcessingRecords, processingFiles, institutionConfig, isSystemAdmin)}
                    </Td>
                    <Td p={2}>
                      {getVectorStoreBadge(file)}
                    </Td>
                    <Td p={2}>
                      <Text fontSize="sm" color="#d1d5db">{file.institution || 'Unknown'}</Text>
                    </Td>
                    <Td p={2}>
                      <Text fontSize="sm" color="#9ca3af">
                        {new Date(file.createdAt).toLocaleDateString()}
                      </Text>
                    </Td>
                    <Td p={2} position="relative">
                      <Menu placement="bottom-end">
                        <MenuButton 
                          as={IconButton} 
                          icon={<FiMoreVertical />} 
                          size="xs" 
                          variant="ghost"
                          color="#9ca3af"
                        />
                        <MenuList 
                          fontSize="sm" 
                          bg="#252525" 
                          border="1px solid #4b5563"
                          zIndex={9999}
                          boxShadow="0 10px 25px rgba(0, 0, 0, 0.3)"
                          portal={true}
                        >
                          <MenuItem 
                            icon={<FiEye />} 
                            onClick={() => onView(file)}
                            fontSize="sm"
                            bg="#252525"
                            color="#d1d5db"
                            _hover={{ bg: "#374151" }}
                          >
                            View Details
                          </MenuItem>
                          {canProcess && (
                            <MenuItem 
                              icon={<FiPlay />} 
                              onClick={() => onProcess(file)}
                              fontSize="sm"
                              bg="#252525"
                              color="#d1d5db"
                              _hover={{ bg: "#374151" }}
                            >
                              Process File
                            </MenuItem>
                          )}
                          <MenuItem 
                            icon={<FiDatabase />} 
                            onClick={() => onAddToVectorStore(file)}
                            fontSize="sm"
                            bg="#252525"
                            color="#d1d5db"
                            _hover={{ bg: "#374151" }}
                          >
                            Add to Vector Store
                          </MenuItem>
                          <MenuItem 
                            onClick={() => onTogglePrivacy && onTogglePrivacy(file)}
                            fontSize="sm"
                            bg="#252525"
                            color="#d1d5db"
                            _hover={{ bg: "#374151" }}
                          >
                            Make {file.isPublic ? 'Private' : 'Public'}
                          </MenuItem>
                          <MenuItem 
                            icon={<FiTrash2 />} 
                            onClick={() => {
                              if (window.confirm(`Delete "${file.originalFilename || file.title}"?`)) {
                                onDelete(file);
                              }
                            }}
                            color="#ef4444"
                            fontSize="sm"
                            bg="#252525"
                            _hover={{ bg: "#374151", color: "#f87171" }}
                          >
                            Delete File
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </CardBody>
    </Card>
  );
};

// Files Tab Component
const FilesTab = () => {
  const [filters, setFilters] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [processingFiles, setProcessingFiles] = useState([]);
  const [vectorStoreModalFiles, setVectorStoreModalFiles] = useState([]);
  const [addingToVectorStore, setAddingToVectorStore] = useState(false);
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const { isOpen: isVectorStoreModalOpen, onOpen: onVectorStoreModalOpen, onClose: onVectorStoreModalClose } = useDisclosure();
  const user = useUser(api);
  const toast = useToast();
  
  // Fetch institution configuration for AI processing status (skip for system admins)
  const isSystemAdmin = checkIsSystemAdmin(user);
  
  // Fetch institution configuration for AI processing status
  const [{ data: institutionConfig, fetching: configFetching, error: configError }, refetchConfig] = useFindFirst(api.institutionConfiguration, {
    filter: !isSystemAdmin && user?.institution ? { institution: { equals: user.institution } } : { institution: { equals: 'SYSTEM_ADMIN_BYPASS' } },
    select: {
      id: true,
      institution: true,
      enableFisdapShiftProcessing: true
    },
    pause: isSystemAdmin // Don't fetch for system admins
  });
  
  // Global action for enabling AI processing
  const [{ }, enableAIProcessing] = useGlobalAction(api.enableInstitutionAIProcessing);
  
  // Build filter object for API call
  const apiFilters = {};
  if (filters.search) {
    // Use search for filename matching
  }
  if (filters.institution) {
    apiFilters.institution = { equals: filters.institution };
  }
  if (filters.status) {
    apiFilters.status = { equals: filters.status };
  }
  if (filters.category) {
    apiFilters.category = { equals: filters.category };
  }

  // Apply processing status filters
  if (filters.processingStatus === 'processing_eligible') {
    apiFilters.AND = [
      { status: { equals: 'active' } },
      { OR: [
        { processingResults: { equals: null } },
        { processingResults: { equals: {} } }
      ]}
    ];
  } else if (filters.processingStatus === 'successfully_processed') {
    apiFilters.processingResults = { notIn: [null, {}] };
  } else if (filters.processingStatus === 'has_extracted_content') {
    apiFilters.extractedText = { isSet: true };
  }

  // Apply quick filters
  if (filters.needsProcessing) {
    apiFilters.AND = [
      { status: { equals: 'active' } },
      { OR: [
        { processingResults: { equals: null } },
        { processingResults: { equals: {} } }
      ]}
    ];
  }
  if (filters.hasResults) {
    apiFilters.processingResults = { notIn: [null, {}] };
  }

  const [{ data: files, fetching, error }, refetch] = useFindMany(api.userFile, {
    filter: Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
    sort: { createdAt: "Descending" },
    first: 50,
    select: {
      id: true,
      originalFilename: true,
      title: true,
      mimeType: true,
      fileSizeBytes: true,
      status: true,
      isPublic: true,
      institution: true,
      createdAt: true,
      category: true,
      processingResults: true,
      extractedText: { markdown: true },
      vectorStore: {
        id: true,
        name: true
      }
    }
  });

  // Query shift processing records to link with files
  const [{ data: shiftProcessingRecords }, refetchShiftProcessing] = useFindMany(api.shiftProcessing, {
    select: {
      id: true,
      userFile: {
        originalFilename: true,
        title: true
      },
      processingStatus: true,
      createdAt: true,
      structuredData: true
    }
  });

  const [{ }, uploadFile] = useAction(api.userFile.create);
  const [{ }, processFile] = useEnqueue(api.processDocument);
  const [{ }, deleteFile] = useAction(api.userFile.delete);
  const [{ }, updateFile] = useAction(api.userFile.update);
  const [{ }, synchronizeVectorStore] = useGlobalAction(api.synchronizeVectorStore);

  const handleUpload = async (file) => {
    try {
      // Check if user is available
      if (!user || !user.id) {
        throw new Error('User not authenticated. Please sign in to upload files.');
      }

      // Convert file to base64
      let fileBase64;
      try {
        fileBase64 = await fileToBase64(file);
      } catch (conversionError) {
        throw new Error('Failed to process file. Please try again.');
      }

      // Generate unique identifiers
      const timestamp = Date.now();
      const uniqueId = `file_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Get user's institution or use default
      const userInstitution = user.institution || 'ElevationEMS';

      // Upload file
      const result = await uploadFile({
        originalFilename: file.name,
        title: file.name,
        mimeType: file.type,
        fileSizeBytes: file.size,
        status: 'active',
        isPublic: true,
        institution: userInstitution,
        category: 'uploaded_document',
        clinicalFileId: uniqueId,
        fileIdentifier: uniqueId,
        studentId: user.id,
        uploadDate: new Date().toISOString(),
        file: {
          base64: fileBase64,
          fileName: file.name,
          mimeType: file.type
        }
      });

      if (result.userFile) {
        toast({
          title: 'File uploaded successfully',
          description: `${file.name} has been uploaded and is ready for processing`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        
        // Refresh the files list
        refetch();
      } else {
        throw new Error('Upload succeeded but no file record was returned');
      }

    } catch (error) {
      console.error('Upload error:', error);
      
      toast({
        title: 'Upload failed',
        description: error.message || 'Unknown error occurred during upload',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      
      throw error;
    }
  };

  const handleView = (file) => {
    // Find linked shift processing record
    const linkedDoc = shiftProcessingRecords?.find(doc => 
      doc.userFileId === file.id || 
      doc.userFile?.originalFilename === file.originalFilename ||
      doc.userFile?.title === file.title
    );

    setPreviewData({
      fileData: file,
      documentData: linkedDoc,
      type: file.type === 'shiftProcessing' ? 'document' : 'file'
    });
    onPreviewOpen();
  };

  const handleProcess = async (file) => {
    if (!file || !file.id) return;

    setProcessingFiles(prev => [...prev, file.id]);
    
    try {
      await processFile({
        userFileId: file.id,
        priority: 'normal',
        useAI: isSystemAdmin || institutionConfig?.enableFisdapShiftProcessing || false
      });

      toast({
        title: 'Processing started',
        description: `${file.originalFilename || file.title} is being processed`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Refresh data
      refetch();
      refetchShiftProcessing();
      
    } catch (error) {
      toast({
        title: 'Processing failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessingFiles(prev => prev.filter(id => id !== file.id));
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete "${file.originalFilename || file.title}"?`)) {
      return;
    }

    try {
      await deleteFile({ id: file.id });
      
      toast({
        title: 'File deleted',
        description: `${file.originalFilename || file.title} has been deleted`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      refetch();
    } catch (error) {
      toast({
        title: 'Delete failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddToVectorStore = (files) => {
    const fileArray = Array.isArray(files) ? files : [files];
    setVectorStoreModalFiles(fileArray);
    onVectorStoreModalOpen();
  };

  const handleVectorStoreConfirm = async (vectorStore) => {
    if (vectorStoreModalFiles.length === 0) return;

    setAddingToVectorStore(true);
    
    try {
      // Update each file to associate with vector store
      await Promise.all(
        vectorStoreModalFiles.map(file => 
          updateFile({
            id: file.id,
            vectorStore: {
              _link: vectorStore.id
            }
          })
        )
      );

      // Update vector store's localFileIds
      const currentIds = Array.isArray(vectorStore.localFileIds) 
        ? vectorStore.localFileIds 
        : (vectorStore.localFileIds ? JSON.parse(vectorStore.localFileIds) : []);
      
      const newFileIds = vectorStoreModalFiles.map(f => f.id);
      const updatedIds = [...new Set([...currentIds, ...newFileIds])];
      
      await api.vectorStore.update(vectorStore.id, {
        localFileIds: updatedIds,
        syncStatus: 'pending_sync'
      });

      toast({
        title: 'Files added to vector store',
        description: `${vectorStoreModalFiles.length} file${vectorStoreModalFiles.length > 1 ? 's' : ''} added to "${vectorStore.name}"`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      refetch();
      setVectorStoreModalFiles([]);
      
    } catch (error) {
      toast({
        title: 'Failed to add files',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAddingToVectorStore(false);
    }
  };

  const handleTogglePrivacy = async (file) => {
    try {
      await updateFile({
        id: file.id,
        isPublic: !file.isPublic
      });
      
      toast({
        title: 'Privacy updated',
        description: `File is now ${!file.isPublic ? 'public' : 'private'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      refetch();
    } catch (error) {
      toast({
        title: 'Update failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleBulkProcess = async () => {
    if (selectedFiles.length === 0) return;

    const filesToProcess = files?.filter(file => selectedFiles.includes(file.id)) || [];
    
    setProcessingFiles(prev => [...prev, ...selectedFiles]);
    
    try {
      await Promise.all(
        filesToProcess.map(file => 
          processFile({
            userFileId: file.id,
            priority: 'normal',
            useAI: isSystemAdmin || institutionConfig?.enableFisdapShiftProcessing || false
          })
        )
      );

      toast({
        title: 'Bulk processing started',
        description: `${selectedFiles.length} files are being processed`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      refetch();
      refetchShiftProcessing();
      setSelectedFiles([]);
      setSelectAll(false);
      
    } catch (error) {
      toast({
        title: 'Bulk processing failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setProcessingFiles(prev => prev.filter(id => !selectedFiles.includes(id)));
    }
  };

  const handleBulkAddToVectorStore = () => {
    if (selectedFiles.length === 0) return;
    
    const filesToAdd = files?.filter(file => selectedFiles.includes(file.id)) || [];
    handleAddToVectorStore(filesToAdd);
  };

  const handleEnableAI = async () => {
    if (!user?.institution) {
      toast({
        title: 'Cannot enable AI processing',
        description: 'No institution found for your account',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await enableAIProcessing({
        institution: user.institution,
        tier: 'basic'
      });

      toast({
        title: 'AI Processing Enabled',
        description: 'Premium AI document analysis is now available',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Refresh config
      refetchConfig();
      
    } catch (error) {
      toast({
        title: 'Failed to enable AI processing',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSelectAllChange = (checked) => {
    setSelectAll(checked);
    if (checked && files) {
      setSelectedFiles(files.map(f => f.id));
    } else {
      setSelectedFiles([]);
    }
  };

  // Apply client-side filtering for search and other complex filters
  const filteredFiles = useMemo(() => {
    if (!files) return [];
    
    let filtered = [...files];
    
    // Search filter
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(file => 
        (file.originalFilename?.toLowerCase().includes(searchTerm)) ||
        (file.title?.toLowerCase().includes(searchTerm)) ||
        (file.extractedText?.markdown?.toLowerCase().includes(searchTerm))
      );
    }
    
    return filtered;
  }, [files, filters]);

  return (
    <VStack spacing={6} align="stretch">
      {/* AI Processing Status Header */}
      <AIProcessingStatusHeader
        user={user}
        institutionConfig={institutionConfig}
        onEnableAI={handleEnableAI}
      />

      {/* Upload Area */}
      <UploadArea onUpload={handleUpload} />
      
      {/* Filters */}
      <FileFilters
        filters={filters}
        onFiltersChange={setFilters}
      />

      {/* Files List */}
      <FileListTable
        files={filteredFiles}
        loading={fetching}
        onProcess={handleProcess}
        onDelete={handleDelete}
        onView={handleView}
        onAddToVectorStore={handleAddToVectorStore}
        onTogglePrivacy={handleTogglePrivacy}
        selectedFiles={selectedFiles}
        onSelectionChange={setSelectedFiles}
        selectAll={selectAll}
        onSelectAllChange={handleSelectAllChange}
        onBulkProcess={handleBulkProcess}
        onBulkAddToVectorStore={handleBulkAddToVectorStore}
        shiftProcessingRecords={shiftProcessingRecords}
        processingFiles={processingFiles}
        institutionConfig={institutionConfig}
        isSystemAdmin={isSystemAdmin}
      />

      {/* Preview Modal */}
      <FilePreviewModal 
        isOpen={isPreviewOpen}
        onClose={onPreviewClose}
        fileData={previewData?.fileData}
        documentData={previewData?.documentData}
        type={previewData?.type}
      />

      {/* Vector Store Selection Modal */}
      <VectorStoreSelectionModal
        isOpen={isVectorStoreModalOpen}
        onClose={() => {
          onVectorStoreModalClose();
          setVectorStoreModalFiles([]);
        }}
        onConfirm={handleVectorStoreConfirm}
        files={vectorStoreModalFiles}
        isBulk={vectorStoreModalFiles.length > 1}
        loading={addingToVectorStore}
      />
    </VStack>
  );
};

// Vector Stores Tab Component
const VectorStoresTab = () => {
  const [selectedStore, setSelectedStore] = useState(null);
  const [deletingStore, setDeletingStore] = useState(null);
  const { isOpen: isViewStoreModalOpen, onOpen: onViewStoreModalOpen, onClose: onViewStoreModalClose } = useDisclosure();
  const { isOpen: isAddFilesModalOpen, onOpen: onAddFilesModalOpen, onClose: onAddFilesModalClose } = useDisclosure();
  const toast = useToast();
  const user = useUser(api);
  const pollingIntervalRef = useRef(null);
  
  // Check admin privileges
  const isAdmin = checkIsAdmin(user);
  const isSystemAdmin = checkIsSystemAdmin(user);
  const canDeleteStores = isSystemAdmin || isAdmin;

  // Fetch vector stores
  const [{ data: vectorStores, fetching, error }, refetch] = useFindMany(api.vectorStore, {
    filter: { isActive: { equals: true } },
    select: {
      id: true,
      name: true,
      description: true,
      status: true,
      syncStatus: true,
      fileCount: true,
      fileSizeBytes: true,
      openaiVectorStoreId: true,
      createdAt: true,
      createdBy: {
        id: true,
        firstName: true,
        lastName: true,
        email: true
      }
    }
  });

  // Delete vector store action
  const [{ }, deleteVectorStore] = useAction(api.vectorStore.delete);

  // Auto-polling effect for in-progress vector stores
  useEffect(() => {
    // Check if any vector stores are in progress
    const hasInProgressStores = vectorStores?.some(store => 
      store.status === 'in_progress' || 
      store.syncStatus === 'pending_sync' || 
      store.syncStatus === 'out_of_sync'
    );

    if (hasInProgressStores && !pollingIntervalRef.current) {
      // Start polling every 4 seconds
      pollingIntervalRef.current = setInterval(() => {
        refetch();
      }, 4000);
      
      console.log('Started polling for vector store updates');
    } else if (!hasInProgressStores && pollingIntervalRef.current) {
      // Stop polling when no stores are in progress
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      
      console.log('Stopped polling - all vector stores are up to date');
    }

    // Cleanup interval on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [vectorStores, refetch]);

  // Cleanup polling interval when component unmounts
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const handleViewStore = (store) => {
    setSelectedStore(store);
    onViewStoreModalOpen();
  };

  const handleAddFiles = (store) => {
    setSelectedStore(store);
    onAddFilesModalOpen();
  };

  const handleDeleteStore = async (store) => {
    if (!canDeleteStores) {
      toast({
        title: 'Permission denied',
        description: 'You do not have permission to delete vector stores',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Show confirmation dialog
    const confirmMessage = `Are you sure you want to delete "${store.name}"?\n\nThis action cannot be undone and will:\n- Remove the vector store permanently\n- Remove all file associations\n- Delete the store from OpenAI\n\nType "DELETE" to confirm:`;
    
    const userInput = window.prompt(confirmMessage);
    if (userInput !== 'DELETE') {
      return;
    }

    setDeletingStore(store.id);

    try {
      await deleteVectorStore({ id: store.id });

      toast({
        title: 'Vector store deleted',
        description: `"${store.name}" has been permanently deleted`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Refresh the vector stores list
      refetch();

    } catch (error) {
      console.error('Error deleting vector store:', error);
      toast({
        title: 'Failed to delete vector store',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeletingStore(null);
    }
  };

  const handleSyncStore = async (store) => {
    // Refresh the list after sync
    setTimeout(() => refetch(), 2000);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / 1024 / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  if (fetching) {
    return (
      <Flex justify="center" p={8}>
        <VStack>
          <Spinner size="lg" />
          <Text>Loading vector stores...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>Failed to load vector stores: {error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Vector Stores Grid */}
      {!vectorStores || vectorStores.length === 0 ? (
        <Card bg="#1e1e1e" border="1px solid #2d2d2d" borderRadius="12px">
          <CardBody>
            <VStack spacing={4} py={8}>
              <FiDatabase size={48} color="#9ca3af" />
              <Text color="#d1d5db" fontSize="lg">No vector stores found</Text>
              <Text color="#9ca3af" fontSize="sm" textAlign="center">
                Vector stores will appear here once files are added to them
              </Text>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {vectorStores.map((store) => (
            <Card key={store.id} bg="#1e1e1e" border="1px solid #2d2d2d" borderRadius="12px" p={0} position="relative">
              <CardBody p={6}>
                <VStack align="stretch" spacing={4}>
                  {/* Store Header */}
                  <HStack justify="space-between" align="start" position="relative">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="lg" fontWeight="600" color="#ffffff" noOfLines={1}>
                        {store.name}
                      </Text>
                      {store.description && (
                        <Text fontSize="sm" color="#9ca3af" noOfLines={2}>
                          {store.description}
                        </Text>
                      )}
                    </VStack>
                    <Menu placement="bottom-end">
                      <MenuButton 
                        as={IconButton} 
                        icon={<FiMoreVertical />} 
                        size="sm" 
                        variant="ghost"
                        color="#9ca3af"
                      />
                      <MenuList 
                        fontSize="sm" 
                        bg="#252525" 
                        border="1px solid #4b5563"
                        zIndex={9999}
                        boxShadow="0 10px 25px rgba(0, 0, 0, 0.3)"
                        portal={true}
                      >
                        <MenuItem 
                          icon={<FiEye />} 
                          onClick={() => handleViewStore(store)}
                          fontSize="sm"
                          bg="#252525"
                          color="#d1d5db"
                          _hover={{ bg: "#374151" }}
                        >
                          View Contents
                        </MenuItem>
                        <MenuItem 
                          icon={<FiFolderPlus />} 
                          onClick={() => handleAddFiles(store)}
                          fontSize="sm"
                          bg="#252525"
                          color="#d1d5db"
                          _hover={{ bg: "#374151" }}
                        >
                          Add Files
                        </MenuItem>
                        {canDeleteStores && (
                          <MenuItem 
                            icon={<FiTrash2 />} 
                            onClick={() => handleDeleteStore(store)}
                            fontSize="sm"
                            bg="#252525"
                            color="#ef4444"
                            _hover={{ bg: "#374151", color: "#f87171" }}
                            isDisabled={deletingStore === store.id}
                          >
                            {deletingStore === store.id ? 'Deleting...' : 'Delete Store'}
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </HStack>

                  {/* Store Stats */}
                  <SimpleGrid columns={2} spacing={4}>
                    <Stat size="sm">
                      <StatLabel color="#9ca3af" fontSize="xs">Files</StatLabel>
                      <StatNumber color="#ffffff" fontSize="md">{store.fileCount || 0}</StatNumber>
                    </Stat>
                    <Stat size="sm">
                      <StatLabel color="#9ca3af" fontSize="xs">Size</StatLabel>
                      <StatNumber color="#ffffff" fontSize="md">{formatFileSize(store.fileSizeBytes)}</StatNumber>
                    </Stat>
                  </SimpleGrid>

                  {/* Store Status */}
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="#9ca3af">Status</Text>
                      <Badge colorScheme={store.status === 'completed' ? 'green' : 'blue'} size="sm">
                        {store.status}
                      </Badge>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="xs" color="#9ca3af">Sync</Text>
                      <Badge colorScheme={store.syncStatus === 'synced' ? 'green' : 'orange'} size="sm">
                        {store.syncStatus?.replace('_', ' ')}
                      </Badge>
                    </VStack>
                  </HStack>

                  {/* Action Buttons */}
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      leftIcon={<FiEye />}
                      onClick={() => handleViewStore(store)}
                      variant="outline"
                      borderColor="#4b5563"
                      color="#d1d5db"
                      _hover={{ bg: "#374151" }}
                      flex={1}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      leftIcon={<FiFolderPlus />}
                      onClick={() => handleAddFiles(store)}
                      colorScheme="blue"
                      flex={1}
                    >
                      Add Files
                    </Button>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* View Store Contents Modal */}
      <ViewStoreContentsModal
        isOpen={isViewStoreModalOpen}
        onClose={onViewStoreModalClose}
        vectorStore={selectedStore}
        onAddFiles={handleAddFiles}
        onSyncStore={handleSyncStore}
      />

      {/* Add Files to Store Modal */}
      <AddFilesToStoreModal
        isOpen={isAddFilesModalOpen}
        onClose={onAddFilesModalClose}
        vectorStore={selectedStore}
        refetchVectorStores={refetch}
        onFilesAdded={() => {
          refetch();
          onAddFilesModalClose();
        }}
      />
    </VStack>
  );
};

// Main Admin File Manager Component
export default function AdminFileManager() {
  const user = useUser(api);
  const [activeTab, setActiveTab] = useState(0);

  // Check admin privileges
  const isAdmin = checkIsAdmin(user);

  if (!user) {
    return (
      <Container maxW="7xl" py={8}>
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text>Loading...</Text>
        </VStack>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container maxW="4xl" py={8}>
        <Alert status="error">
          <AlertIcon />
          <Box>
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You need administrator privileges to access the file manager.
            </AlertDescription>
          </Box>
        </Alert>
      </Container>
    );
  }

  return (
    <Box bg="#111111" minH="100vh" color="#ffffff">
      <Container maxW="7xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={2}>
              <Heading size="xl" color="#ffffff">
                File Manager
              </Heading>
              <Text color="#9ca3af">
                Manage uploaded files, process documents, and organize vector stores
              </Text>
            </VStack>
            <HStack spacing={3}>
              <Link href="/admin" style={{ textDecoration: 'none' }}>
                <Button
                  leftIcon={<FiChevronRight />}
                  variant="outline"
                  borderColor="#4b5563"
                  color="#d1d5db"
                  _hover={{ bg: "#374151" }}
                  size="sm"
                >
                  Back to Admin
                </Button>
              </Link>
            </HStack>
          </HStack>

          {/* Tab Navigation */}
          <Tabs 
            index={activeTab} 
            onChange={setActiveTab}
            variant="enclosed"
            bg="#1e1e1e"
            borderRadius="12px"
            border="1px solid #2d2d2d"
          >
            <TabList bg="#252525" borderTopRadius="12px">
              <Tab 
                color="#9ca3af" 
                _selected={{ 
                  color: "#ffffff", 
                  bg: "#3b82f6",
                  borderColor: "#3b82f6"
                }}
                _hover={{ color: "#d1d5db" }}
              >
                <HStack>
                  <FiFile />
                  <Text>Files</Text>
                </HStack>
              </Tab>
              <Tab 
                color="#9ca3af" 
                _selected={{ 
                  color: "#ffffff", 
                  bg: "#3b82f6",
                  borderColor: "#3b82f6"
                }}
                _hover={{ color: "#d1d5db" }}
              >
                <HStack>
                  <FiDatabase />
                  <Text>Vector Stores</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels bg="#111111" borderBottomRadius="12px">
              <TabPanel p={6}>
                <FilesTab />
              </TabPanel>
              <TabPanel p={6}>
                <VectorStoresTab />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </Box>
  );
}
